import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { MapContainer, TileLayer, Marker, Polyline, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import api from '../services/api';
import { toast } from 'react-toastify';
import { getCachedCoords, requestCoords } from '../utils/geolocation';
import { isMobileSharedLink, openInApp } from '../utils/openInApp';
import ReportBlockMenu from '../components/social/ReportBlockMenu';
import LoadError from '../components/common/LoadError';
import { photoThumb } from '../utils/media';

// Small inline share glyph (Feather "share-2") reused by route & landmark detail.
const ShareIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

// Marks the "avoid motorways" routing preference on the route builder.
const LeafIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z" />
    <path d="M2 21c0-3 1.85-5.36 5.08-6" />
  </svg>
);

// Copy a shareable web link (which redirects to the app when installed).
const shareLink = (query) => {
  const url = `${window.location.origin}/routes?${query}`;
  navigator.clipboard.writeText(url).then(() => toast.success('Link copied to clipboard'));
};

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const ROUTE_TAGS = ['Scenic', 'Touge', 'Highway', 'Mountains', 'Coastal', 'Winding', 'Forest', 'Romantic', 'Lookout', 'Urban', 'Photo Spot', 'Sunset', 'Meet Spot'];

const SORT_OPTIONS = [
  { key: 'featured',  label: 'Featured',   icon: '✦' },
  { key: 'nearest',   label: 'Nearest',    icon: '◎' },
  { key: 'distance',  label: 'Distance',   icon: '↕' },
  { key: 'reviews',   label: 'Reviews',    icon: '↓' },
  { key: 'rating',    label: 'Rating',     icon: '↓' },
];

const DIFFICULTY_COLORS = { easy: '#4caf7d', moderate: '#f5a623', challenging: '#e84545' };

const TAG_COLORS = {
  Scenic: '#4a9eff', Touge: '#e84545', Highway: '#8892b0', Mountains: '#7c6ff7',
  Coastal: '#4caf7d', Winding: '#f5a623', Forest: '#56ab56', Romantic: '#e06fa0', Lookout: '#20c0c0', Urban: '#e0a020',
  'Photo Spot': '#ffd97d',
  Sunset: '#ff9a76', 'Meet Spot': '#5fc6c0',
  // Landmark-only tags
  Canyon: '#ff7c3f', Desert: '#d4a44c', Twisties: '#c678dd',
  Beach: '#35c4d7', Cafe: '#b5794f', Hike: '#8fae3c', Waterfall: '#2f9ec4',
};

// Pick a color for a route/landmark on the map. Touge wins if present, else first tag.
const pickItemColor = (item) => {
  const tags = item?.tags || [];
  if (tags.includes('Touge')) return TAG_COLORS.Touge;
  return TAG_COLORS[tags[0]] || '#8892b0';
};

// ─── Leaflet custom icons ─────────────────────────────────────────────────────
const makeIcon = (color, letter) => L.divIcon({
  className: '',
  html: `<div style="
    width:28px;height:28px;border-radius:50% 50% 50% 0;
    background:${color};border:2px solid #fff;
    transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;
    box-shadow:0 2px 8px rgba(0,0,0,0.5);
  "><span style="transform:rotate(45deg);color:#fff;font-size:11px;font-weight:700;line-height:1;">${letter}</span></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

const startIcon  = makeIcon('#4caf7d', 'A');
const endIcon    = makeIcon('#e84545', 'B');
const stopIcon   = (n) => makeIcon('#f5a623', n);
const placingIcon = makeIcon('#3b82f6', '·');

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Decimate coords to stay within Mapbox Static API URL limit (~8k chars)
const decimateCoords = (coords, maxPoints = 50) => {
  if (coords.length <= maxPoints) return coords;
  const step = Math.ceil(coords.length / maxPoints);
  const result = coords.filter((_, i) => i % step === 0);
  const last = coords[coords.length - 1];
  if (result[result.length - 1] !== last) result.push(last);
  return result;
};

export const routeStaticMapUrl = (route) => {
  const sLat = parseFloat(route.startLatitude);
  const sLon = parseFloat(route.startLongitude);
  const eLat = parseFloat(route.endLatitude);
  const eLon = parseFloat(route.endLongitude);
  if (!sLat || !sLon || !eLat || !eLon) return null;

  const startPin = `pin-s-a+4caf7d(${sLon},${sLat})`;
  const endPin   = `pin-s-b+e84545(${eLon},${eLat})`;

  // If we have a saved route geometry, draw it as a path overlay
  if (route.routeGeometry?.coordinates?.length > 1) {
    const simplified = decimateCoords(route.routeGeometry.coordinates);
    const geojson = JSON.stringify({
      type: 'Feature',
      properties: { stroke: '#1a73e8', 'stroke-width': 4, 'stroke-opacity': 0.9 },
      geometry: { type: 'LineString', coordinates: simplified },
    });
    const encoded = encodeURIComponent(geojson);
    const overlays = `geojson(${encoded}),${startPin},${endPin}`;
    return `https://api.mapbox.com/styles/v1/mapbox/outdoors-v12/static/${overlays}/auto/400x220?padding=50&access_token=${MAPBOX_TOKEN}`;
  }

  // Fallback: just pins, no route line
  return `https://api.mapbox.com/styles/v1/mapbox/outdoors-v12/static/${startPin},${endPin}/auto/400x220?padding=60&access_token=${MAPBOX_TOKEN}`;
};

const fmtKm = (km) => km ? `${parseFloat(km).toFixed(1)} km` : null;

// Build a Google Maps directions URL that approximates the saved route geometry
// by sampling up to 7 interior waypoints from the polyline, then appending any
// named stop waypoints.  Google Maps accepts max 9 waypoints via URL.
// Exported so an event's inline cruise path can open in maps exactly like a route.
export const buildGoogleMapsUrl = (route) => {
  const sLat = parseFloat(route.startLatitude);
  const sLon = parseFloat(route.startLongitude);
  const eLat = parseFloat(route.endLatitude);
  const eLon = parseFloat(route.endLongitude);
  if (!sLat || !eLat) return null;

  const base = `https://www.google.com/maps/dir/?api=1&origin=${sLat},${sLon}&destination=${eLat},${eLon}&travelmode=driving`;

  const wps = [];

  // Sample interior points from saved route geometry (skip first and last — those are origin/dest)
  const coords = route.routeGeometry?.coordinates;
  if (coords && coords.length > 2) {
    const interior = coords.slice(1, -1);
    const maxSamples = 7;
    const step = Math.max(1, Math.floor(interior.length / maxSamples));
    for (let i = 0; i < interior.length && wps.length < maxSamples; i += step) {
      const [lon, lat] = interior[i];
      wps.push(`${parseFloat(lat).toFixed(5)},${parseFloat(lon).toFixed(5)}`);
    }
  } else {
    // Fallback: use saved named waypoints if no geometry
    (route.waypoints || [])
      .filter(w => w.latitude && w.longitude)
      .slice(0, 9)
      .forEach(w => wps.push(`${parseFloat(w.latitude)},${parseFloat(w.longitude)}`));
  }

  return wps.length ? `${base}&waypoints=${encodeURIComponent(wps.join('|'))}` : base;
};

// Haversine distance in km between two lat/lon points
const haversine = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLon/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Featured ranking — works for both routes and landmarks.
//
// Quality (rating, reviews, photos, route geometry, recency) is gated by a
// "local" multiplier that protects local results: full weight within ~100 km,
// linear decay out to ~800 km, zero beyond. A small additive proximity bonus
// nudges the closest items up within the local zone. Net effect: a great
// route in Turkey ranks below a mediocre route 30 km from a UK user, but
// inside the local zone (same state / nearby cities) ratings dominate.
const featuredScore = (item, userCoords) => {
  const rating    = parseFloat(item.avgRating || 0) / 5;
  const reviews   = Math.log((item.reviewCount || 0) + 1) / Math.log(51);
  const hasPhotos = (item.photos?.length > 0) ? 1 : 0;
  const hasRoute  = item.routeGeometry ? 1 : 0;
  const ageDays   = (Date.now() - new Date(item.createdAt)) / 86400000;
  const recency   = Math.max(0, 1 - ageDays / 180);

  // Base quality, up to ~0.90
  const quality =
    rating    * 0.35 +
    reviews   * 0.25 +
    hasPhotos * 0.08 +
    hasRoute  * 0.07 +
    recency   * 0.15;

  // Routes: startLatitude/startLongitude. Landmarks: latitude/longitude.
  const itemLat = parseFloat(item.startLatitude ?? item.latitude);
  const itemLng = parseFloat(item.startLongitude ?? item.longitude);

  if (!userCoords) return quality;                              // no anchor → quality only
  if (!isFinite(itemLat) || !isFinite(itemLng)) return quality * 0.3; // unlocated item → deprioritise

  const distKm = haversine(userCoords.lat, userCoords.lon, itemLat, itemLng);

  // Local gate: full weight ≤100 km, linear decay to 0 at 800 km
  let local;
  if (distKm <= 100)      local = 1;
  else if (distKm >= 800) local = 0;
  else                    local = 1 - (distKm - 100) / 700;

  // Additive proximity bonus — small but enough to break ties within local
  const proximityBonus = Math.max(0, 1 - distKm / 800) * 0.20;

  return quality * local + proximityBonus;
};

const sortRoutes = (routes, sortBy, userCoords) => {
  const arr = [...routes];
  switch (sortBy) {
    case 'featured':  return arr.sort((a, b) => featuredScore(b, userCoords) - featuredScore(a, userCoords));
    case 'nearest':
      if (!userCoords) return arr;
      return arr.sort((a, b) => {
        const da = (a.startLatitude && a.startLongitude) ? haversine(userCoords.lat, userCoords.lon, parseFloat(a.startLatitude), parseFloat(a.startLongitude)) : Infinity;
        const db = (b.startLatitude && b.startLongitude) ? haversine(userCoords.lat, userCoords.lon, parseFloat(b.startLatitude), parseFloat(b.startLongitude)) : Infinity;
        return da - db;
      });
    case 'distance':  return arr.sort((a, b) => parseFloat(b.distanceKm || 0) - parseFloat(a.distanceKm || 0));
    case 'reviews':   return arr.sort((a, b) => (b.reviewCount || 0) - (a.reviewCount || 0));
    case 'rating':    return arr.sort((a, b) => parseFloat(b.avgRating || 0) - parseFloat(a.avgRating || 0));
    default: return arr;
  }
};

// Route alternative colors (index → color)
const ALT_COLORS = ['#1a73e8', '#f5a623', '#4caf7d', '#e06fa0'];

// ─── Mapbox Directions ────────────────────────────────────────────────────────
const fetchDirections = async (points, avoidMotorway = false) => {
  if (points.length < 2) return null;
  const coords = points.map(p => `${p.longitude},${p.latitude}`).join(';');
  const exclude = avoidMotorway ? '&exclude=motorway' : '';
  try {
    const res = await fetch(
      `https://api.mapbox.com/directions/v5/mapbox/driving/${coords}?geometries=geojson&alternatives=true&overview=full${exclude}&access_token=${MAPBOX_TOKEN}`
    );
    const data = await res.json();
    if (!data.routes?.length) return null;
    return data.routes;
  } catch { return null; }
};

// ─── Mapbox Reverse Geocoding ─────────────────────────────────────────────────
const reverseGeocode = async (lat, lng) => {
  try {
    const res = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${MAPBOX_TOKEN}&limit=1&types=address,place,locality,neighborhood`
    );
    const data = await res.json();
    return data.features?.[0]?.place_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  } catch {
    return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  }
};

// ─── Mapbox Geocoding ─────────────────────────────────────────────────────────
const geocodeSearch = async (q) => {
  if (!q.trim()) return [];
  try {
    const res = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=${MAPBOX_TOKEN}&autocomplete=true&limit=6&types=address,place,locality,neighborhood`
    );
    const data = await res.json();
    return data.features || [];
  } catch { return []; }
};

// ─── Star Rating ─────────────────────────────────────────────────────────────
const StarRating = ({ value, onChange, size = 18 }) => {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: 'flex', gap: 2, lineHeight: 1 }}>
      {[1, 2, 3, 4, 5].map(star => (
        <span key={star}
          style={{ fontSize: size, cursor: onChange ? 'pointer' : 'default', color: star <= (hover || Math.round(value)) ? '#f5a623' : '#3a3e4a', userSelect: 'none' }}
          onMouseEnter={() => onChange && setHover(star)}
          onMouseLeave={() => onChange && setHover(0)}
          onClick={() => onChange && onChange(star)}
        >★</span>
      ))}
    </div>
  );
};

const AmenityBar = ({ label, value }) => (
  <div className="amenity-bar-row">
    <span className="amenity-bar-label">{label}</span>
    <div className="amenity-bar-track">
      <div className="amenity-bar-fill" style={{ width: `${(value / 5) * 100}%` }} />
    </div>
    <span className="amenity-bar-score">{value ? parseFloat(value).toFixed(1) : '—'}</span>
  </div>
);

// ─── Search Input with Dropdown ───────────────────────────────────────────────
const SearchInput = ({ value, onChange, onSelect, placeholder, color }) => {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();
  const wrapperRef = useRef();

  useEffect(() => {
    const handler = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChange = (e) => {
    const q = e.target.value;
    onChange(q);
    clearTimeout(debounceRef.current);
    if (!q.trim()) { setResults([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      const features = await geocodeSearch(q);
      setResults(features);
      setOpen(features.length > 0);
      setLoading(false);
    }, 380);
  };

  const handleSelect = (place) => {
    const [lon, lat] = place.geometry.coordinates;
    onChange(place.place_name);
    onSelect({ address: place.place_name, latitude: lat, longitude: lon });
    setResults([]); setOpen(false);
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', flex: 1 }}>
      <div style={{ position: 'relative' }}>
        {color && <div style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />}
        <input
          className="event-form-input"
          style={{ paddingLeft: color ? 26 : 12 }}
          value={value}
          onChange={handleChange}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={placeholder}
          autoComplete="off"
        />
        {loading && <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}><div className="loading-spinner" style={{ width: 13, height: 13, borderWidth: 2 }} /></div>}
      </div>
      {open && results.length > 0 && (
        <ul style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#1a2030', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, zIndex: 3000, listStyle: 'none', margin: 0, padding: 0, maxHeight: 200, overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
          {results.map((place, i) => (
            <li key={i} onMouseDown={() => handleSelect(place)}
              style={{ padding: '9px 14px', cursor: 'pointer', fontSize: '0.81rem', color: '#c9ced6', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >{place.place_name}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ─── Map internals (must be inside MapContainer) ──────────────────────────────
const MapController = ({ form, activePlacingRef, onPlace, dirRoutes, selectedRouteIdx, setSelectedRouteIdx }) => {
  const map = useMap();

  useMapEvents({
    click(e) {
      const placing = activePlacingRef.current;
      if (placing === null || placing === undefined) return;
      const { lat, lng } = e.latlng;
      onPlace(placing, lat, lng);
    },
  });

  // Fit map to all known points whenever they change
  useEffect(() => {
    const pts = [];
    if (form.startLatitude && form.startLongitude) pts.push([form.startLatitude, form.startLongitude]);
    form.stops.forEach(s => { if (s.latitude && s.longitude) pts.push([s.latitude, s.longitude]); });
    if (form.endLatitude && form.endLongitude) pts.push([form.endLatitude, form.endLongitude]);
    if (pts.length === 1) { map.flyTo(pts[0], 13, { animate: true, duration: 0.8 }); }
    else if (pts.length >= 2) { map.flyToBounds(L.latLngBounds(pts), { padding: [50, 50], animate: true, duration: 0.8 }); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.startLatitude, form.startLongitude, form.endLatitude, form.endLongitude, form.stops.length]);

  return (
    <>
      {/* Alternative route lines — draw unselected first (behind), then selected on top */}
      {dirRoutes.map((r, i) => i !== selectedRouteIdx && (
        <Polyline key={`alt-${i}`} positions={r.geometry.coordinates.map(([ln, lt]) => [lt, ln])}
          pathOptions={{ color: ALT_COLORS[i] || '#888', weight: 4, lineCap: 'round', opacity: 0.45, dashArray: '6 5' }}
          eventHandlers={{ click: () => setSelectedRouteIdx(i) }}
        />
      ))}
      {/* Selected route line */}
      {dirRoutes[selectedRouteIdx] && (
        <Polyline positions={dirRoutes[selectedRouteIdx].geometry.coordinates.map(([ln, lt]) => [lt, ln])}
          pathOptions={{ color: ALT_COLORS[selectedRouteIdx] || '#1a73e8', weight: 5, lineCap: 'round', opacity: 1 }}
        />
      )}
      {/* Start marker */}
      {form.startLatitude && form.startLongitude && (
        <Marker position={[form.startLatitude, form.startLongitude]} icon={startIcon} draggable
          eventHandlers={{ dragend: (e) => { const { lat, lng } = e.target.getLatLng(); onPlace('start', lat, lng); } }}
        />
      )}
      {/* Stop markers */}
      {form.stops.map((s, i) => s.latitude && s.longitude && (
        <Marker key={`stop-${i}`} position={[s.latitude, s.longitude]} icon={stopIcon(i + 1)} draggable
          eventHandlers={{ dragend: (e) => { const { lat, lng } = e.target.getLatLng(); onPlace(i, lat, lng); } }}
        />
      ))}
      {/* End marker */}
      {form.endLatitude && form.endLongitude && (
        <Marker position={[form.endLatitude, form.endLongitude]} icon={endIcon} draggable
          eventHandlers={{ dragend: (e) => { const { lat, lng } = e.target.getLatLng(); onPlace('end', lat, lng); } }}
        />
      )}
    </>
  );
};

// ─── Step 1 — Details ─────────────────────────────────────────────────────────
const Step1 = ({ form, set, onNext, onClose }) => (
  <div className="route-form-step">
    <div className="event-modal-header">
      <h2 className="event-modal-title">Route Details</h2>
      <button className="event-modal-close" onClick={onClose}>✕</button>
    </div>
    <div className="event-modal-form">
      <div className="event-form-row">
        <label className="event-form-label">Route Name</label>
        <input className="event-form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Pacific Highway Coastal Run" />
      </div>

      <div className="event-form-row">
        <label className="event-form-label">Tags</label>
        <div className="event-type-pills">
          {ROUTE_TAGS.map(tag => {
            const active = form.tags.includes(tag);
            const color = TAG_COLORS[tag];
            return (
              <button key={tag} type="button"
                className={`event-type-pill${active ? ' active' : ''}`}
                style={active ? { borderColor: color, color } : {}}
                onClick={() => set('tags', active ? form.tags.filter(t => t !== tag) : [...form.tags, tag])}
              >{tag}</button>
            );
          })}
        </div>
      </div>

      <div className="event-form-row">
        <label className="event-form-label">Description <span style={{ color: '#5a657a', fontWeight: 400 }}>— optional</span></label>
        <textarea className="event-form-input event-form-textarea" value={form.description} onChange={e => set('description', e.target.value)} placeholder="What makes this route special?" rows={4} />
      </div>

      <div className="event-modal-actions">
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="btn-primary"
          disabled={!form.name.trim()}
          onClick={onNext}
        >Next: Set Locations →</button>
      </div>
    </div>
  </div>
);

// ─── Step 2 — Map & Locations ─────────────────────────────────────────────────
const Step2 = ({ form, setForm, onBack, onSubmit, submitting, onClose }) => {
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const [activePlacing, setActivePlacingState] = useState(null); // 'start' | 'end' | stopIndex
  const activePlacingRef = useRef(null); // ref so useMapEvents always sees current value
  const setActivePlacing = (val) => {
    const v = typeof val === 'function' ? val(activePlacingRef.current) : val;
    activePlacingRef.current = v;
    setActivePlacingState(v);
  };

  const [dirRoutes, setDirRoutes] = useState([]);
  const [selectedRouteIdx, setSelectedRouteIdx] = useState(0);
  const [avoidMotorway, setAvoidMotorway] = useState(false);
  const dirFetchRef = useRef();

  // Place a pin and reverse-geocode the address
  const handlePlace = useCallback(async (key, lat, lng) => {
    setActivePlacing(null);
    const address = await reverseGeocode(lat, lng);
    if (key === 'start') {
      setForm(f => ({ ...f, startLatitude: lat, startLongitude: lng, startAddress: address, startInput: address }));
    } else if (key === 'end') {
      setForm(f => ({ ...f, endLatitude: lat, endLongitude: lng, endAddress: address, endInput: address }));
    } else {
      setForm(f => {
        const stops = [...f.stops];
        stops[key] = { ...stops[key], latitude: lat, longitude: lng, address, input: address };
        return { ...f, stops };
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setForm]);

  // Fetch directions whenever pins or avoidMotorway changes
  const triggerDirections = useCallback(async (f, avoid) => {
    const pts = [];
    if (f.startLatitude && f.startLongitude) pts.push({ latitude: f.startLatitude, longitude: f.startLongitude });
    f.stops.forEach(s => { if (s.latitude && s.longitude) pts.push({ latitude: s.latitude, longitude: s.longitude }); });
    if (f.endLatitude && f.endLongitude) pts.push({ latitude: f.endLatitude, longitude: f.endLongitude });
    if (pts.length < 2) { setDirRoutes([]); return; }
    const routes = await fetchDirections(pts, avoid);
    if (routes) {
      setDirRoutes(routes);
      setSelectedRouteIdx(0);
      // auto-fill distance from selected route
      const distM = routes[0].distance;
      setForm(prev => ({
        ...prev,
        distanceKm: (distM / 1000).toFixed(1),
        routeGeometry: routes[0].geometry,
      }));
    }
  }, [setForm]);

  useEffect(() => {
    clearTimeout(dirFetchRef.current);
    dirFetchRef.current = setTimeout(() => triggerDirections(form, avoidMotorway), 600);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.startLatitude, form.startLongitude, form.endLatitude, form.endLongitude, form.stops, avoidMotorway]);

  // When user selects a different alternative route
  const handleSelectRoute = (idx) => {
    setSelectedRouteIdx(idx);
    const distM = dirRoutes[idx].distance;
    setForm(f => ({ ...f, distanceKm: (distM / 1000).toFixed(1), routeGeometry: dirRoutes[idx].geometry }));
  };

  const addStop = () => setForm(f => ({ ...f, stops: [...f.stops, { id: Date.now(), input: '', address: '', latitude: null, longitude: null }] }));
  const removeStop = (i) => setForm(f => ({ ...f, stops: f.stops.filter((_, idx) => idx !== i) }));

  const placingLabel = activePlacing === 'start' ? 'start' : activePlacing === 'end' ? 'end' : typeof activePlacing === 'number' ? `stop ${activePlacing + 1}` : null;

  return (
    <div className="route-form-step route-form-step--map">
      <div className="event-modal-header" style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
        <h2 className="event-modal-title">Set Locations</h2>
        <button className="event-modal-close" onClick={onClose}>✕</button>
      </div>

      {/* Location inputs */}
      <div className="route-location-inputs">
        {/* Start */}
        <div className="route-location-row">
          <div className="route-location-dot" style={{ background: '#4caf7d' }} />
          <SearchInput
            value={form.startInput}
            onChange={v => set('startInput', v)}
            onSelect={({ address, latitude, longitude }) =>
              setForm(f => ({ ...f, startAddress: address, startLatitude: latitude, startLongitude: longitude }))
            }
            placeholder="Start location…"
            color="#4caf7d"
          />
          <button
            type="button"
            className={`route-pin-btn${activePlacing === 'start' ? ' active' : ''}`}
            title="Click map to place pin"
            onClick={() => setActivePlacing(p => p === 'start' ? null : 'start')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
          </button>
        </div>

        {/* Stops */}
        {form.stops.map((stop, i) => (
          <div key={stop.id} className="route-location-row">
            <div className="route-location-dot" style={{ background: '#f5a623' }} />
            <SearchInput
              value={stop.input}
              onChange={v => setForm(f => { const stops = [...f.stops]; stops[i] = { ...stops[i], input: v }; return { ...f, stops }; })}
              onSelect={({ address, latitude, longitude }) =>
                setForm(f => { const stops = [...f.stops]; stops[i] = { ...stops[i], address, latitude, longitude }; return { ...f, stops }; })
              }
              placeholder={`Stop ${i + 1}…`}
              color="#f5a623"
            />
            <button
              type="button"
              className={`route-pin-btn${activePlacing === i ? ' active' : ''}`}
              title="Click map to place pin"
              onClick={() => setActivePlacing(p => p === i ? null : i)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
            </button>
            <button type="button" className="route-remove-stop" onClick={() => removeStop(i)}>✕</button>
          </div>
        ))}

        {/* End */}
        <div className="route-location-row">
          <div className="route-location-dot" style={{ background: '#e84545' }} />
          <SearchInput
            value={form.endInput}
            onChange={v => set('endInput', v)}
            onSelect={({ address, latitude, longitude }) =>
              setForm(f => ({ ...f, endAddress: address, endLatitude: latitude, endLongitude: longitude }))
            }
            placeholder="End location…"
            color="#e84545"
          />
          <button
            type="button"
            className={`route-pin-btn${activePlacing === 'end' ? ' active' : ''}`}
            title="Click map to place pin"
            onClick={() => setActivePlacing(p => p === 'end' ? null : 'end')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
          </button>
        </div>

        {/* Add stop */}
        <button type="button" className="route-add-stop-btn" onClick={addStop}>+ Add Stop</button>
      </div>

      {/* Map */}
      <div className="route-map-container" style={{ cursor: activePlacing !== null ? 'crosshair' : 'grab' }}>
        {placingLabel && (
          <div className="route-placing-hint">Click anywhere on the map to place the <strong>{placingLabel}</strong> pin</div>
        )}
        <MapContainer
          center={[-25.2744, 133.7751]}
          zoom={4}
          style={{ width: '100%', height: '100%' }}
          zoomControl={true}
          zoomAnimation={false}
          fadeAnimation={false}
        >
          <TileLayer
            url={`https://api.mapbox.com/styles/v1/mapbox/outdoors-v12/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`}
            tileSize={512}
            zoomOffset={-1}
            attribution='&copy; <a href="https://www.mapbox.com/">Mapbox</a>'
          />
          <MapController
            form={form}
            activePlacingRef={activePlacingRef}
            onPlace={handlePlace}
            dirRoutes={dirRoutes}
            selectedRouteIdx={selectedRouteIdx}
            setSelectedRouteIdx={handleSelectRoute}
          />
        </MapContainer>
      </div>

      {/* Route alternatives + distance */}
      <div className="route-alternatives">
        {dirRoutes.length > 0 && <div className="route-alt-label">Route options:</div>}
        {dirRoutes.map((r, i) => {
          const fastestDur = dirRoutes[0].duration;
          const extraMin = Math.round((r.duration - fastestDur) / 60);
          const label = i === 0
            ? 'Fastest'
            : extraMin > 0 ? `+${extraMin} min` : 'Alternative';
          const color = ALT_COLORS[i] || '#888';
          return (
            <button key={i} type="button"
              className={`route-alt-btn${selectedRouteIdx === i ? ' active' : ''}`}
              style={selectedRouteIdx === i ? { borderColor: color, color } : {}}
              onClick={() => handleSelectRoute(i)}
            >
              <span className="route-alt-swatch" style={{ background: color }} />
              {label} — {(r.distance / 1000).toFixed(1)} km · {Math.round(r.duration / 60)} min
            </button>
          );
        })}
        <button
          type="button"
          className={`route-alt-btn route-alt-scenic-toggle${avoidMotorway ? ' active' : ''}`}
          style={avoidMotorway ? { borderColor: '#4caf7d', color: '#4caf7d', marginLeft: 'auto' } : { marginLeft: 'auto' }}
          onClick={() => setAvoidMotorway(v => !v)}
        >
          <span className="route-alt-scenic-main">
            <LeafIcon /> {avoidMotorway ? 'Scenic on' : 'Scenic'}
          </span>
          <span className="route-alt-scenic-sub">avoids motorways</span>
        </button>
      </div>

      {form.distanceKm && (
        <div className="route-distance-display">
          <strong>{form.distanceKm} km</strong> · distance auto-calculated from route
        </div>
      )}

      <div className="event-modal-actions" style={{ padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
        <button type="button" className="btn-secondary" onClick={onBack}>← Back</button>
        <button type="button" className="btn-primary"
          disabled={submitting || (!form.startLatitude && !form.endLatitude)}
          onClick={onSubmit}
        >{submitting ? 'Saving…' : 'Save Route'}</button>
      </div>
    </div>
  );
};

// ─── Create Route Modal ───────────────────────────────────────────────────────
export const CreateRouteModal = ({ onClose, onCreate }) => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', tags: [],
    startInput: '', startAddress: '', startLatitude: null, startLongitude: null,
    endInput: '', endAddress: '', endLatitude: null, endLongitude: null,
    stops: [],
    distanceKm: '', routeGeometry: null,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post('/routes', {
        name: form.name, description: form.description || null, tags: form.tags,
        startAddress: form.startAddress || form.startInput || null,
        startLatitude: form.startLatitude, startLongitude: form.startLongitude,
        endAddress: form.endAddress || form.endInput || null,
        endLatitude: form.endLatitude, endLongitude: form.endLongitude,
        waypoints: form.stops.filter(s => s.latitude).map(s => ({ address: s.address, latitude: s.latitude, longitude: s.longitude })),
        distanceKm: form.distanceKm ? parseFloat(form.distanceKm) : null,
        routeGeometry: form.routeGeometry,
      });
      onCreate(data.route);
      toast.success('Route created!');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create route');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="event-modal-overlay">
      <div className={`event-modal${step === 2 ? ' event-modal--map' : ''}`}>
        {step === 1
          ? <Step1 form={form} set={set} onNext={() => setStep(2)} onClose={onClose} />
          : <Step2 form={form} setForm={setForm} onBack={() => setStep(1)} onSubmit={submit} submitting={submitting} onClose={onClose} />
        }
      </div>
    </div>
  );
};

// ─── Route Card ───────────────────────────────────────────────────────────────
const RouteCard = ({ route, onClick }) => {
  const mapImg = route.thumbnailUrl || routeStaticMapUrl(route);

  return (
    <div className="route-card" onClick={onClick}>
      <div className="route-card-map">
        {mapImg ? (
          <img src={mapImg} alt={route.name} loading="lazy" />
        ) : (
          <div className="route-card-map-placeholder">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="6" cy="19" r="3" /><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15" /><circle cx="18" cy="5" r="3" /></svg>
            <span>No preview</span>
          </div>
        )}
        <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(26,115,232,0.85)', borderRadius: 4, padding: '2px 7px', fontSize: '0.7rem', color: '#fff', fontWeight: 600, letterSpacing: '0.03em' }}>Route</div>
        {route.tags?.length > 0 && (
          <div className="route-card-tags">
            {route.tags.slice(0, 2).map(tag => (
              <span key={tag} className="route-tag-chip" style={{ color: TAG_COLORS[tag], borderColor: TAG_COLORS[tag] }}>{tag}</span>
            ))}
            {route.tags.length > 2 && <span className="route-tag-chip" style={{ color: '#8892b0', borderColor: '#8892b0' }}>+{route.tags.length - 2}</span>}
          </div>
        )}
      </div>
      <div className="route-card-body">
        <h3 className="route-card-name">{route.name}</h3>
        <div className="route-card-meta">
          {route.distanceKm && <span className="route-card-detail">{fmtKm(route.distanceKm)}</span>}
          {route.startAddress && <span className="route-card-detail" style={{ color: '#5a657a' }}>{route.startAddress.split(',')[0]}</span>}
        </div>
        {route.reviewCount > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
            <StarRating value={parseFloat(route.avgRating)} size={13} />
            <span style={{ fontSize: '0.72rem', color: '#5a657a' }}>{parseFloat(route.avgRating).toFixed(1)} ({route.reviewCount})</span>
          </div>
        )}
        {route.description && <p className="route-card-desc">{route.description}</p>}
        {route.creator && <div className="route-card-creator" style={{ marginTop: 8 }}>by {route.creator.username}</div>}
      </div>
    </div>
  );
};

// ─── Route Detail / Edit Modal ────────────────────────────────────────────────
const RouteDetailModal = ({ route: initialRoute, currentUserId, onClose, onDelete, onUpdate }) => {
  const [route, setRoute] = useState(initialRoute);
  const [editing, setEditing] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const photoInputRef = useRef(null);
  // Rich photo objects (id, imageUrl, uploader, …) fetched from the dedicated
  // endpoint so each photo can be individually reported. `route.photos` from
  // the detail endpoint is just URL strings, kept only for the tab badge.
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [myReview, setMyReview] = useState(null);
  const [reviewsLoaded, setReviewsLoaded] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 0, comment: '', fuelRating: 0, foodRating: 0, toiletRating: 0, viewsRating: 0 });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [editStep, setEditStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: initialRoute.name || '',
    description: initialRoute.description || '',
    tags: initialRoute.tags || [],
    startInput: initialRoute.startAddress || '',
    startAddress: initialRoute.startAddress || '',
    startLatitude: initialRoute.startLatitude ? parseFloat(initialRoute.startLatitude) : null,
    startLongitude: initialRoute.startLongitude ? parseFloat(initialRoute.startLongitude) : null,
    endInput: initialRoute.endAddress || '',
    endAddress: initialRoute.endAddress || '',
    endLatitude: initialRoute.endLatitude ? parseFloat(initialRoute.endLatitude) : null,
    endLongitude: initialRoute.endLongitude ? parseFloat(initialRoute.endLongitude) : null,
    stops: (initialRoute.waypoints || []).map(w => ({ id: Date.now() + Math.random(), input: w.address || '', ...w })),
    distanceKm: initialRoute.distanceKm ? String(parseFloat(initialRoute.distanceKm)) : '',
    routeGeometry: initialRoute.routeGeometry || null,
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Route name is required');
    setSubmitting(true);
    try {
      const { data } = await api.put(`/routes/${route.id}`, {
        name: form.name, description: form.description || null, tags: form.tags,
        startAddress: form.startAddress || form.startInput || null,
        startLatitude: form.startLatitude, startLongitude: form.startLongitude,
        endAddress: form.endAddress || form.endInput || null,
        endLatitude: form.endLatitude, endLongitude: form.endLongitude,
        waypoints: form.stops.filter(s => s.latitude).map(s => ({ address: s.address, latitude: s.latitude, longitude: s.longitude })),
        distanceKm: form.distanceKm ? parseFloat(form.distanceKm) : null,
        routeGeometry: form.routeGeometry,
      });
      const updated = { ...route, ...data.route };
      setRoute(updated);
      onUpdate(updated);
      toast.success('Route updated!');
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update route');
    } finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this route? This cannot be undone.')) return;
    try {
      await api.delete(`/routes/${route.id}`);
      toast.success('Route deleted');
      onDelete(route.id);
      onClose();
    } catch { toast.error('Failed to delete route'); }
  };

  // Load reviews once on open
  useEffect(() => {
    api.get(`/routes/${initialRoute.id}/reviews`).then(({ data }) => {
      setReviews(data.reviews || []);
      setMyReview(data.myReview || null);
      if (data.myReview) {
        setReviewForm({
          rating: parseFloat(data.myReview.rating) || 0,
          comment: data.myReview.comment || '',
          fuelRating: parseFloat(data.myReview.fuelRating) || 0,
          foodRating: parseFloat(data.myReview.foodRating) || 0,
          toiletRating: parseFloat(data.myReview.toiletRating) || 0,
          viewsRating: parseFloat(data.myReview.viewsRating) || 0,
        });
      }
      setReviewsLoaded(true);
    }).catch(() => setReviewsLoaded(true));
  }, [initialRoute.id]);

  const handleSubmitReview = async () => {
    if (!reviewForm.rating) return toast.error('Please give an overall star rating');
    setSubmittingReview(true);
    try {
      const { data } = await api.post(`/routes/${route.id}/reviews`, {
        rating: reviewForm.rating,
        comment: reviewForm.comment || null,
        fuelRating: reviewForm.fuelRating || null,
        foodRating: reviewForm.foodRating || null,
        toiletRating: reviewForm.toiletRating || null,
        viewsRating: reviewForm.viewsRating || null,
      });
      setMyReview(data.review);
      setReviews(prev => {
        const without = prev.filter(r => r.id !== data.review.id);
        return [data.review, ...without];
      });
      const updated = { ...route, ...data.stats };
      setRoute(updated);
      onUpdate(updated);
      toast.success(myReview ? 'Review updated!' : 'Review submitted!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit review');
    } finally { setSubmittingReview(false); }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Delete your review?')) return;
    try {
      const { data } = await api.delete(`/routes/${route.id}/reviews/${reviewId}`);
      setReviews(prev => prev.filter(r => r.id !== reviewId));
      setMyReview(null);
      setReviewForm({ rating: 0, comment: '', fuelRating: 0, foodRating: 0, toiletRating: 0, viewsRating: 0 });
      const updated = { ...route, ...data.stats };
      setRoute(updated);
      onUpdate(updated);
    } catch { toast.error('Failed to delete review'); }
  };

  useEffect(() => {
    if (lightboxIdx === null) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft')  setLightboxIdx(i => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setLightboxIdx(i => Math.min(galleryPhotos.length - 1, i + 1));
      if (e.key === 'Escape')     setLightboxIdx(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxIdx, galleryPhotos.length]);

  useEffect(() => {
    api.get(`/routes/${initialRoute.id}/photos`)
      .then(({ data }) => setGalleryPhotos(data.photos || []))
      .catch(() => {});
  }, [initialRoute.id]);

  const handleAddPhoto = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const fd = new FormData();
    files.forEach(f => fd.append('photos', f));
    try {
      const { data } = await api.post(`/routes/${route.id}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setGalleryPhotos(prev => [...prev, ...(data.photos || [])]);
    } catch { toast.error('Failed to upload photos'); }
    e.target.value = '';
  };

  const handleDeletePhoto = async (photo) => {
    if (!window.confirm('Remove this photo?')) return;
    try {
      await api.delete(`/routes/${route.id}/photos/${photo.id}`);
      setGalleryPhotos(prev => prev.filter(p => p.id !== photo.id));
      if (lightboxIdx !== null) setLightboxIdx(null);
    } catch { toast.error('Failed to remove photo'); }
  };

  const handleReportedPhoto = ({ targetId }) => {
    setGalleryPhotos(prev => prev.filter(p => p.id !== targetId));
    if (lightboxIdx !== null) setLightboxIdx(null);
  };

  const [activeTab, setActiveTab] = useState('details');
  const isOwner = route.creatorId === currentUserId;
  const mapImg = route.thumbnailUrl || routeStaticMapUrl(route);
  const googleMapsUrl = buildGoogleMapsUrl(route);

  if (editing) {
    return (
      <div className="event-modal-overlay">
        <div className={`event-modal${editStep === 2 ? ' event-modal--map' : ''}`}>
          {editStep === 1 ? (
            <div className="route-form-step">
              <div className="event-modal-header">
                <h2 className="event-modal-title">Edit Route</h2>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <button className="btn-danger btn-sm" onClick={handleDelete}>Delete</button>
                  <button className="event-modal-close" onClick={onClose}>✕</button>
                </div>
              </div>
              <div className="event-modal-form">
                <div className="event-form-row">
                  <label className="event-form-label">Route Name</label>
                  <input className="event-form-input" value={form.name} onChange={e => set('name', e.target.value)} />
                </div>
                <div className="event-form-row">
                  <label className="event-form-label">Tags</label>
                  <div className="event-type-pills">
                    {ROUTE_TAGS.map(tag => {
                      const active = form.tags.includes(tag);
                      const color = TAG_COLORS[tag];
                      return (
                        <button key={tag} type="button"
                          className={`event-type-pill${active ? ' active' : ''}`}
                          style={active ? { borderColor: color, color } : {}}
                          onClick={() => set('tags', active ? form.tags.filter(t => t !== tag) : [...form.tags, tag])}
                        >{tag}</button>
                      );
                    })}
                  </div>
                </div>
                <div className="event-form-row">
                  <label className="event-form-label">Description</label>
                  <textarea className="event-form-input event-form-textarea" value={form.description} onChange={e => set('description', e.target.value)} rows={4} />
                </div>
                <div className="event-modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
                  <button type="button" className="btn-primary" disabled={!form.name.trim()} onClick={() => setEditStep(2)}>Next: Locations →</button>
                </div>
              </div>
            </div>
          ) : (
            <Step2
              form={form}
              setForm={setForm}
              onBack={() => setEditStep(1)}
              onSubmit={handleSave}
              submitting={submitting}
              onClose={onClose}
            />
          )}
        </div>
      </div>
    );
  }

  // View mode
  return (
    <div className="event-detail-overlay" onClick={onClose}>
      <div className="route-detail-popup" onClick={e => e.stopPropagation()}>

        {/* Hero map image */}
        {mapImg && (
          <div className="route-detail-map-top">
            <img src={mapImg} alt={route.name} />
          </div>
        )}

        {/* Header row */}
        <div className="route-detail-header">
          <div className="route-detail-header-left">
            <h2 className="route-detail-title">{route.name}</h2>
            <div className="route-detail-meta">
              {route.creator && (
                <span className="route-detail-creator">by {route.creator.username}</span>
              )}
              {route.distanceKm && (
                <span className="route-detail-distance">{fmtKm(route.distanceKm)}</span>
              )}
              {route.reviewCount > 0 && (
                <span className="route-detail-rating">
                  <StarRating value={parseFloat(route.avgRating)} size={12} />
                  <span>{parseFloat(route.avgRating).toFixed(1)}</span>
                  <span className="route-detail-rating-count">({route.reviewCount})</span>
                </span>
              )}
            </div>
            <div className="route-detail-tags">
              {route.tags?.map(tag => (
                <span key={tag} className="route-tag-chip" style={{ color: TAG_COLORS[tag], borderColor: TAG_COLORS[tag] }}>{tag}</span>
              ))}
            </div>
          </div>
          <div className="route-detail-header-right">
            {isOwner && <button className="event-edit-btn" onClick={() => setEditing(true)}>Edit</button>}
            <button
              className="event-share-btn"
              title="Share route"
              onClick={() => shareLink(`route=${route.id}`)}
            >
              <ShareIcon />
              Share
            </button>
            {!isOwner && route.creatorId && (
              <ReportBlockMenu
                targetType="route"
                targetId={route.id}
                targetUserId={route.creatorId}
                targetLabel="route"
                onReported={() => { onDelete?.(route.id); onClose(); }}
                onBlocked={() => { onDelete?.(route.id); onClose(); }}
              />
            )}
            <button className="event-modal-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="route-detail-tabs">
          {[
            { key: 'details', label: 'Details' },
            { key: 'reviews', label: `Reviews${route.reviewCount > 0 ? ` (${route.reviewCount})` : ''}` },
            { key: 'gallery', label: `Gallery${galleryPhotos.length > 0 ? ` (${galleryPhotos.length})` : ''}` },
          ].map(tab => (
            <button
              key={tab.key}
              className={`route-detail-tab${activeTab === tab.key ? ' active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >{tab.label}</button>
          ))}
        </div>

        {/* Tab content */}
        <div className="route-detail-body">

          {/* ── Details ── */}
          {activeTab === 'details' && (
            <div className="route-tab-details">
              {route.description && <p className="route-detail-desc">{route.description}</p>}

              <div className="route-detail-points">
                <div className="route-detail-point">
                  <div className="route-detail-point-dot route-detail-point-dot--start" />
                  <div>
                    <div className="route-detail-point-label">Start</div>
                    <div className="route-detail-point-address">{route.startAddress || 'Unknown'}</div>
                  </div>
                </div>
                {(route.waypoints || []).map((wp, i) => (
                  <React.Fragment key={i}>
                    <div className="route-detail-connector" />
                    <div className="route-detail-point">
                      <div className="route-detail-point-dot" style={{ background: '#f5a623' }} />
                      <div>
                        <div className="route-detail-point-label">Stop {i + 1}</div>
                        <div className="route-detail-point-address">{wp.address || 'Unknown'}</div>
                      </div>
                    </div>
                  </React.Fragment>
                ))}
                <div className="route-detail-connector" />
                <div className="route-detail-point">
                  <div className="route-detail-point-dot route-detail-point-dot--end" />
                  <div>
                    <div className="route-detail-point-label">End</div>
                    <div className="route-detail-point-address">{route.endAddress || 'Unknown'}</div>
                  </div>
                </div>
              </div>

              {googleMapsUrl && (
                <div className="route-detail-actions">
                  <a className="btn-primary" href={googleMapsUrl} target="_blank" rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', padding: '9px 18px', fontSize: '0.85rem', fontWeight: 600 }}>
                    Open in Google Maps ↗
                  </a>
                </div>
              )}
            </div>
          )}

          {/* ── Reviews ── */}
          {activeTab === 'reviews' && (
            <div className="route-tab-reviews">

              {/* Aggregate amenity bars */}
              {route.reviewCount > 0 && route.amenityScore != null && (
                <div className="route-amenities-agg">
                  <p className="route-amenities-agg-title">Amenities</p>
                  {[
                    ['Fuel', reviews, 'fuelRating'],
                    ['Food', reviews, 'foodRating'],
                    ['Toilets', reviews, 'toiletRating'],
                    ['Views', reviews, 'viewsRating'],
                  ].map(([label, revs, key]) => {
                    const rated = revs.filter(r => r[key]);
                    const avg = rated.length ? rated.reduce((s, r) => s + parseFloat(r[key]), 0) / rated.length : null;
                    return avg != null ? <AmenityBar key={key} label={label} value={avg} /> : null;
                  })}
                </div>
              )}

              {/* Write / edit review */}
              <div className="route-review-form">
                <div className="route-review-form-title">{myReview ? 'Your Review' : 'Write a Review'}</div>
                <div className="route-review-form-row">
                  <span className="route-review-form-label">Rating</span>
                  <StarRating value={reviewForm.rating} onChange={v => setReviewForm(f => ({ ...f, rating: v }))} size={20} />
                </div>
                <div className="route-review-amenities">
                  <p className="route-review-amenities-title">Amenity ratings <span>(optional)</span></p>
                  {[['fuelRating', 'Fuel'], ['foodRating', 'Food'], ['toiletRating', 'Toilets'], ['viewsRating', 'Views']].map(([key, label]) => (
                    <div key={key} className="route-review-form-row">
                      <span className="route-review-form-label">{label}</span>
                      <StarRating value={reviewForm[key]} onChange={v => setReviewForm(f => ({ ...f, [key]: v }))} size={16} />
                    </div>
                  ))}
                </div>
                <textarea
                  className="event-form-input event-form-textarea"
                  value={reviewForm.comment}
                  onChange={e => setReviewForm(f => ({ ...f, comment: e.target.value }))}
                  placeholder="Share your experience on this route…"
                  rows={3}
                  style={{ marginTop: 10 }}
                />
                <div className="route-review-form-actions">
                  <button className="btn-primary btn-sm" onClick={handleSubmitReview} disabled={submittingReview || !reviewForm.rating}>
                    {submittingReview ? 'Saving…' : myReview ? 'Update' : 'Submit'}
                  </button>
                  {myReview && (
                    <button className="btn-ghost btn-sm" onClick={() => handleDeleteReview(myReview.id)}>Remove review</button>
                  )}
                </div>
              </div>

              {/* Review list */}
              {reviewsLoaded && reviews.length > 0 ? (
                <div className="route-review-list">
                  {reviews.map(r => {
                    const rAuthorId = r.author?.id || r.userId;
                    const isOwnReview = rAuthorId === currentUserId;
                    return (
                      <div key={r.id} className="route-review-card">
                        <div className="route-review-card-header">
                          <div className="route-review-author">
                            {r.author?.profileImageUrl
                              ? <img src={r.author.profileImageUrl} alt={r.author.username} />
                              : <div className="route-review-avatar-placeholder">{r.author?.username?.[0]?.toUpperCase()}</div>
                            }
                            <span>{r.author?.username}</span>
                          </div>
                          <div className="route-review-card-right">
                            <StarRating value={parseFloat(r.rating)} size={13} />
                            <span className="route-review-date">
                              {new Date(r.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                            {!isOwnReview && rAuthorId && (
                              <ReportBlockMenu
                                targetType="route_review"
                                targetId={r.id}
                                targetUserId={rAuthorId}
                                targetLabel="review"
                                onReported={() => setReviews(prev => prev.filter(x => x.id !== r.id))}
                                onBlocked={(blockedUserId) => setReviews(prev => prev.filter(x => (x.author?.id || x.userId) !== blockedUserId))}
                                triggerSize="small"
                              />
                            )}
                          </div>
                        </div>
                        {(r.fuelRating || r.foodRating || r.toiletRating || r.viewsRating) && (
                          <div className="route-review-amenity-pills">
                            {r.fuelRating && <span className="route-review-amenity-pill">Fuel {parseFloat(r.fuelRating).toFixed(1)}</span>}
                            {r.foodRating && <span className="route-review-amenity-pill">Food {parseFloat(r.foodRating).toFixed(1)}</span>}
                            {r.toiletRating && <span className="route-review-amenity-pill">Toilets {parseFloat(r.toiletRating).toFixed(1)}</span>}
                            {r.viewsRating && <span className="route-review-amenity-pill">Views {parseFloat(r.viewsRating).toFixed(1)}</span>}
                          </div>
                        )}
                        {r.comment && <p className="route-review-comment">{r.comment}</p>}
                      </div>
                    );
                  })}
                </div>
              ) : reviewsLoaded ? (
                <p className="route-empty-hint">No reviews yet — be the first.</p>
              ) : null}
            </div>
          )}

          {/* ── Gallery ── */}
          {activeTab === 'gallery' && (
            <div className="route-tab-gallery">
              <div className="route-gallery-upload">
                <button className="btn-ghost btn-sm" onClick={() => photoInputRef.current?.click()}>+ Add Photos</button>
                <input ref={photoInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleAddPhoto} />
              </div>
              {galleryPhotos.length > 0 ? (
                <div className="route-photos-grid">
                  {galleryPhotos.map((p, i) => {
                    const canDelete = isOwner || p.userId === currentUserId;
                    const isMine = p.userId === currentUserId;
                    return (
                      <div key={p.id} className="route-photo-thumb" style={{ position: 'relative' }}>
                        <img src={photoThumb(p)} alt="" onClick={() => setLightboxIdx(i)} />
                        {!isMine && (
                          <div style={{ position: 'absolute', top: 4, right: 4 }} onClick={e => e.stopPropagation()}>
                            <ReportBlockMenu
                              targetType="route_photo"
                              targetId={p.id}
                              targetUserId={p.userId}
                              targetLabel="photo"
                              hideBlock={!p.uploader}
                              triggerSize="small"
                              onReported={handleReportedPhoto}
                              onBlocked={(uid) => setGalleryPhotos(prev => prev.filter(x => x.userId !== uid))}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="route-empty-hint">No photos yet — add some to show off this route.</p>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Lightbox */}
      {lightboxIdx !== null && galleryPhotos[lightboxIdx] && (
        <div className="route-lightbox" onClick={() => setLightboxIdx(null)}>
          <div className="route-lightbox-inner" onClick={e => e.stopPropagation()}>
            <img src={galleryPhotos[lightboxIdx].imageUrl} alt="Route" />
            <div className="route-lightbox-controls">
              <button onClick={() => setLightboxIdx(i => Math.max(0, i - 1))} disabled={lightboxIdx === 0}>‹</button>
              <span>{lightboxIdx + 1} / {galleryPhotos.length}</span>
              <button onClick={() => setLightboxIdx(i => Math.min(galleryPhotos.length - 1, i + 1))} disabled={lightboxIdx === galleryPhotos.length - 1}>›</button>
              {(isOwner || galleryPhotos[lightboxIdx].userId === currentUserId) && (
                <button className="route-lightbox-delete" onClick={() => handleDeletePhoto(galleryPhotos[lightboxIdx])}>Delete</button>
              )}
            </div>
            <button className="route-lightbox-close" onClick={() => setLightboxIdx(null)}>✕</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Landmark helpers ─────────────────────────────────────────────────────────
export const landmarkStaticMapUrl = (lm) => {
  const lat = parseFloat(lm.latitude);
  const lon = parseFloat(lm.longitude);
  if (!lat || !lon) return null;
  const pin = `pin-s-star+7c6ff7(${lon},${lat})`;
  return `https://api.mapbox.com/styles/v1/mapbox/outdoors-v12/static/${pin}/${lon},${lat},13/400x220?access_token=${MAPBOX_TOKEN}`;
};

const MapPinController = ({ placing, onPlace, latitude, longitude }) => {
  const map = useMap();
  useMapEvents({
    click(e) { if (!placing) return; onPlace(e.latlng.lat, e.latlng.lng); },
  });
  useEffect(() => {
    if (latitude && longitude) map.flyTo([latitude, longitude], 13, { animate: true, duration: 0.8 });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latitude, longitude]);
  const lmPinIcon = L.divIcon({
    className: '',
    html: `<div style="width:28px;height:28px;border-radius:50% 50% 50% 0;background:#7c6ff7;border:2px solid #fff;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.5);"><span style="transform:rotate(45deg);color:#fff;font-size:14px;line-height:1;">★</span></div>`,
    iconSize: [28, 28], iconAnchor: [14, 28],
  });
  return latitude && longitude ? (
    <Marker position={[latitude, longitude]} icon={lmPinIcon} draggable
      eventHandlers={{ dragend: (e) => { const { lat, lng } = e.target.getLatLng(); onPlace(lat, lng); } }}
    />
  ) : null;
};

const CreateLandmarkModal = ({ onClose, onCreate }) => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', tags: [], locationInput: '', address: '', latitude: null, longitude: null });
  const [placing, setPlacing] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handlePlace = useCallback(async (lat, lng) => {
    setPlacing(false);
    const address = await reverseGeocode(lat, lng);
    setForm(f => ({ ...f, latitude: lat, longitude: lng, address, locationInput: address }));
  }, []);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post('/landmarks', { name: form.name, description: form.description || null, tags: form.tags, address: form.address, latitude: form.latitude, longitude: form.longitude });
      toast.success('Landmark created!');
      onCreate({ ...data.landmark, _type: 'landmark' });
      onClose();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to create landmark'); }
    finally { setSubmitting(false); }
  };

  if (step === 1) return (
    <div className="event-modal-overlay">
      <div className="event-modal" style={{ maxWidth: 520 }}>
        <div className="event-modal-header">
          <h2 className="event-modal-title">Add Landmark</h2>
          <button className="event-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="event-modal-form">
          <div className="event-form-row">
            <label className="event-form-label">Landmark Name</label>
            <input className="event-form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Govetts Leap Lookout" />
          </div>
          <div className="event-form-row">
            <label className="event-form-label">Tags</label>
            <div className="event-type-pills">
              {ROUTE_TAGS.map(tag => {
                const active = form.tags.includes(tag);
                const color = TAG_COLORS[tag];
                return (
                  <button key={tag} type="button" className={`event-type-pill${active ? ' active' : ''}`}
                    style={active ? { borderColor: color, color } : {}}
                    onClick={() => set('tags', active ? form.tags.filter(t => t !== tag) : [...form.tags, tag])}
                  >{tag}</button>
                );
              })}
            </div>
          </div>
          <div className="event-form-row">
            <label className="event-form-label">Description <span style={{ color: '#5a657a', fontWeight: 400 }}>— optional</span></label>
            <textarea className="event-form-input event-form-textarea" value={form.description} onChange={e => set('description', e.target.value)} placeholder="What makes this place special?" rows={4} />
          </div>
          <div className="event-modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="button" className="btn-primary" disabled={!form.name.trim()} onClick={() => setStep(2)}>Next: Set Location →</button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="event-modal-overlay">
      <div className="event-modal event-modal--map">
        <div className="route-form-step">
          <div className="event-modal-header" style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
            <h2 className="event-modal-title">Set Location</h2>
            <button className="event-modal-close" onClick={onClose}>✕</button>
          </div>
          <div className="route-location-inputs">
            <div className="route-location-row">
              <div className="route-location-dot" style={{ background: '#7c6ff7' }} />
              <SearchInput value={form.locationInput} onChange={v => set('locationInput', v)}
                onSelect={({ address, latitude, longitude }) => setForm(f => ({ ...f, address, latitude, longitude, locationInput: address }))}
                placeholder="Search for a location…" />
              <button type="button" className={`route-pin-btn${placing ? ' active' : ''}`} title="Click map to place pin" onClick={() => setPlacing(p => !p)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
              </button>
            </div>
          </div>
          <div className="route-map-container" style={{ cursor: placing ? 'crosshair' : 'grab' }}>
            {placing && <div className="route-placing-hint">Click anywhere on the map to place the pin</div>}
            <MapContainer center={[-25.2744, 133.7751]} zoom={4} style={{ width: '100%', height: '100%' }} zoomControl={true} zoomAnimation={false} fadeAnimation={false}>
              <TileLayer url={`https://api.mapbox.com/styles/v1/mapbox/outdoors-v12/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`} tileSize={512} zoomOffset={-1} attribution='&copy; Mapbox' />
              <MapPinController placing={placing} onPlace={handlePlace} latitude={form.latitude} longitude={form.longitude} />
            </MapContainer>
          </div>
          <div className="event-modal-actions" style={{ padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
            <button type="button" className="btn-secondary" onClick={() => setStep(1)}>← Back</button>
            <button type="button" className="btn-primary" disabled={submitting || !form.latitude} onClick={handleSubmit}>{submitting ? 'Saving…' : 'Save Landmark'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const LandmarkCard = ({ landmark, onClick }) => {
  const mapUrl = landmarkStaticMapUrl(landmark);

  return (
    <div className="route-card" onClick={() => onClick(landmark)} style={{ cursor: 'pointer' }}>
      <div className="route-card-map">
        {mapUrl
          ? <img src={mapUrl} alt={landmark.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5a657a', fontSize: '0.8rem' }}>No location set</div>
        }
        <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(124,111,247,0.85)', borderRadius: 4, padding: '2px 7px', fontSize: '0.7rem', color: '#fff', fontWeight: 600, letterSpacing: '0.03em' }}>Landmark</div>
        {landmark.tags?.length > 0 && (
          <div className="route-card-tags">
            {landmark.tags.slice(0, 2).map(tag => (
              <span key={tag} className="route-tag-chip" style={{ color: TAG_COLORS[tag], borderColor: TAG_COLORS[tag] }}>{tag}</span>
            ))}
            {landmark.tags.length > 2 && <span className="route-tag-chip" style={{ color: '#8892b0', borderColor: '#8892b0' }}>+{landmark.tags.length - 2}</span>}
          </div>
        )}
      </div>
      <div className="route-card-body">
        <div className="route-card-title">{landmark.name}</div>
        <div className="route-card-meta">
          {landmark.reviewCount > 0 && <span style={{ color: '#f5a623', fontSize: '0.78rem' }}>★ {parseFloat(landmark.avgRating).toFixed(1)} ({landmark.reviewCount})</span>}
          {landmark.address && <span style={{ color: '#5a657a', fontSize: '0.75rem' }}>{landmark.address.split(',').slice(-2).join(',').trim()}</span>}
        </div>
        <div className="route-card-creator" style={{ marginTop: 8 }}>
          <div className="route-creator-avatar" style={landmark.creator?.profileImageUrl ? { backgroundImage: `url(${landmark.creator.profileImageUrl})` } : {}} />
          <span>{landmark.creator?.username}</span>
        </div>
      </div>
    </div>
  );
};

const LandmarkDetailModal = ({ landmark: initialLandmark, currentUserId, onClose, onDelete, onUpdate }) => {
  const [landmark, setLandmark] = useState(initialLandmark);
  const [activeTab, setActiveTab] = useState('details');
  const [reviews, setReviews] = useState([]);
  const [myReview, setMyReview] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 0, comment: '' });
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editStep, setEditStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [editPlacing, setEditPlacing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: initialLandmark.name || '',
    description: initialLandmark.description || '',
    tags: initialLandmark.tags || [],
    locationInput: initialLandmark.address || '',
    address: initialLandmark.address || '',
    latitude: initialLandmark.latitude ? parseFloat(initialLandmark.latitude) : null,
    longitude: initialLandmark.longitude ? parseFloat(initialLandmark.longitude) : null,
  });
  const photoInputRef = useRef();
  const isOwner = currentUserId === landmark.creatorId;

  const setEdit = (k, v) => setEditForm(f => ({ ...f, [k]: v }));

  const handleEditPlace = useCallback(async (lat, lng) => {
    setEditPlacing(false);
    const address = await reverseGeocode(lat, lng);
    setEditForm(f => ({ ...f, latitude: lat, longitude: lng, address, locationInput: address }));
  }, []);

  const handleSave = async () => {
    if (!editForm.name.trim()) return toast.error('Name is required');
    setSubmitting(true);
    try {
      const { data } = await api.put(`/landmarks/${landmark.id}`, {
        name: editForm.name,
        description: editForm.description || null,
        tags: editForm.tags,
        address: editForm.address || editForm.locationInput || null,
        latitude: editForm.latitude,
        longitude: editForm.longitude,
      });
      const updated = { ...landmark, ...data.landmark };
      setLandmark(updated);
      if (onUpdate) onUpdate(updated);
      toast.success('Landmark updated!');
      setEditing(false);
      setEditStep(1);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally { setSubmitting(false); }
  };

  const loadReviews = useCallback(async () => {
    try {
      const { data } = await api.get(`/landmarks/${landmark.id}/reviews`);
      setReviews(data.reviews || []);
      setMyReview(data.myReview || null);
      if (data.myReview) setReviewForm({ rating: parseFloat(data.myReview.rating), comment: data.myReview.comment || '' });
    } catch {}
  }, [landmark.id]);

  useEffect(() => { if (activeTab === 'reviews') loadReviews(); }, [activeTab, loadReviews]);

  useEffect(() => {
    api.get(`/landmarks/${landmark.id}/photos`)
      .then(({ data }) => setGalleryPhotos(data.photos || []))
      .catch(() => {});
  }, [landmark.id]);

  const handleSubmitReview = async () => {
    if (!reviewForm.rating) return toast.error('Please select a rating');
    setSubmittingReview(true);
    try {
      const { data } = await api.post(`/landmarks/${landmark.id}/reviews`, reviewForm);
      setReviews(prev => [data.review, ...prev.filter(r => r.id !== data.review.id)]);
      setMyReview(data.review);
      setLandmark(prev => ({ ...prev, avgRating: data.stats.avgRating, reviewCount: data.stats.reviewCount }));
      toast.success(myReview ? 'Review updated' : 'Review submitted');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to submit review'); }
    finally { setSubmittingReview(false); }
  };

  const handleDeleteReview = async (reviewId) => {
    try {
      const { data } = await api.delete(`/landmarks/${landmark.id}/reviews/${reviewId}`);
      setReviews(prev => prev.filter(r => r.id !== reviewId));
      setMyReview(null);
      setReviewForm({ rating: 0, comment: '' });
      setLandmark(prev => ({ ...prev, avgRating: data.stats.avgRating, reviewCount: data.stats.reviewCount }));
    } catch { toast.error('Failed to delete review'); }
  };

  const handleAddPhoto = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const fd = new FormData();
    files.forEach(f => fd.append('photos', f));
    try {
      const { data } = await api.post(`/landmarks/${landmark.id}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setGalleryPhotos(prev => [...prev, ...(data.photos || [])]);
    } catch { toast.error('Failed to upload photo'); }
    e.target.value = '';
  };

  const handleDeletePhoto = async (photo) => {
    if (!window.confirm('Remove this photo?')) return;
    try {
      await api.delete(`/landmarks/${landmark.id}/photos/${photo.id}`);
      setGalleryPhotos(prev => prev.filter(p => p.id !== photo.id));
      if (lightboxIdx !== null) setLightboxIdx(null);
    } catch { toast.error('Failed to delete photo'); }
  };

  const handleReportedPhoto = ({ targetId }) => {
    setGalleryPhotos(prev => prev.filter(p => p.id !== targetId));
    if (lightboxIdx !== null) setLightboxIdx(null);
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this landmark?')) return;
    setDeleting(true);
    try {
      await api.delete(`/landmarks/${landmark.id}`);
      toast.success('Landmark deleted');
      onDelete(landmark.id);
      onClose();
    } catch { toast.error('Failed to delete'); setDeleting(false); }
  };

  const mapUrl = landmarkStaticMapUrl(landmark);
  const starStr = (n) => '★'.repeat(Math.round(n)) + '☆'.repeat(5 - Math.round(n));

  // ── Edit mode ──
  if (editing) {
    return (
      <div className="event-modal-overlay">
        <div className={`event-modal${editStep === 2 ? ' event-modal--map' : ''}`}>
          {editStep === 1 ? (
            <div className="route-form-step">
              <div className="event-modal-header">
                <h2 className="event-modal-title">Edit Landmark</h2>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <button className="event-modal-close" onClick={() => { setEditing(false); setEditStep(1); }}>✕</button>
                </div>
              </div>
              <div className="event-modal-form">
                <div className="event-form-row">
                  <label className="event-form-label">Name</label>
                  <input className="event-form-input" value={editForm.name} onChange={e => setEdit('name', e.target.value)} />
                </div>
                <div className="event-form-row">
                  <label className="event-form-label">Tags</label>
                  <div className="event-type-pills">
                    {ROUTE_TAGS.map(tag => {
                      const active = editForm.tags.includes(tag);
                      const color = TAG_COLORS[tag];
                      return (
                        <button key={tag} type="button"
                          className={`event-type-pill${active ? ' active' : ''}`}
                          style={active ? { borderColor: color, color } : {}}
                          onClick={() => setEdit('tags', active ? editForm.tags.filter(t => t !== tag) : [...editForm.tags, tag])}
                        >{tag}</button>
                      );
                    })}
                  </div>
                </div>
                <div className="event-form-row">
                  <label className="event-form-label">Description</label>
                  <textarea className="event-form-input event-form-textarea" value={editForm.description} onChange={e => setEdit('description', e.target.value)} rows={4} />
                </div>
                <div className="event-modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => { setEditing(false); setEditStep(1); }}>Cancel</button>
                  <button type="button" className="btn-primary" disabled={!editForm.name.trim()} onClick={() => setEditStep(2)}>Next: Location →</button>
                </div>
              </div>
            </div>
          ) : (
            <div className="route-form-step">
              <div className="event-modal-header" style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
                <h2 className="event-modal-title">Edit Location</h2>
                <button className="event-modal-close" onClick={() => { setEditing(false); setEditStep(1); }}>✕</button>
              </div>
              <div className="route-location-inputs">
                <div className="route-location-row">
                  <div className="route-location-dot" style={{ background: '#7c6ff7' }} />
                  <SearchInput value={editForm.locationInput} onChange={v => setEdit('locationInput', v)}
                    onSelect={({ address, latitude, longitude }) => setEditForm(f => ({ ...f, address, latitude, longitude, locationInput: address }))}
                    placeholder="Search for a location…" />
                  <button type="button" className={`route-pin-btn${editPlacing ? ' active' : ''}`} title="Click map to place pin" onClick={() => setEditPlacing(p => !p)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                  </button>
                </div>
              </div>
              <div className="route-map-container" style={{ cursor: editPlacing ? 'crosshair' : 'grab' }}>
                {editPlacing && <div className="route-placing-hint">Click anywhere on the map to place the pin</div>}
                <MapContainer center={editForm.latitude ? [editForm.latitude, editForm.longitude] : [-25.2744, 133.7751]} zoom={editForm.latitude ? 13 : 4} style={{ width: '100%', height: '100%' }} zoomControl={true} zoomAnimation={false} fadeAnimation={false}>
                  <TileLayer url={`https://api.mapbox.com/styles/v1/mapbox/outdoors-v12/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`} tileSize={512} zoomOffset={-1} attribution='&copy; Mapbox' />
                  <MapPinController placing={editPlacing} onPlace={handleEditPlace} latitude={editForm.latitude} longitude={editForm.longitude} />
                </MapContainer>
              </div>
              <div className="event-modal-actions" style={{ padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
                <button type="button" className="btn-secondary" onClick={() => setEditStep(1)}>← Back</button>
                <button type="button" className="btn-primary" disabled={submitting} onClick={handleSave}>{submitting ? 'Saving…' : 'Save Changes'}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="event-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="route-detail-popup">
        <div className="route-detail-map-top">
          {mapUrl ? <img src={mapUrl} alt={landmark.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5a657a' }}>No location set</div>}
          <button className="event-modal-close" onClick={onClose} style={{ position: 'absolute', top: 12, right: 12, zIndex: 10, background: 'rgba(0,0,0,0.55)', border: 'none', color: '#fff', borderRadius: 6, width: 32, height: 32, cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>
        <div className="route-detail-header">
          <div>
            <div style={{ marginBottom: 4 }}>
              <span style={{ background: 'rgba(124,111,247,0.18)', color: '#7c6ff7', borderRadius: 4, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.04em' }}>LANDMARK</span>
            </div>
            <h2 className="route-detail-title">{landmark.name}</h2>
            <div className="route-detail-meta">
              {landmark.reviewCount > 0 && <span style={{ color: '#f5a623' }}>★ {parseFloat(landmark.avgRating).toFixed(1)} ({landmark.reviewCount} review{landmark.reviewCount !== 1 ? 's' : ''})</span>}
              {landmark.address && <span style={{ color: '#8892b0' }}>{landmark.address}</span>}
              <span style={{ color: '#5a657a' }}>by {landmark.creator?.username}</span>
            </div>
            {landmark.tags?.length > 0 && (
              <div className="route-detail-tags" style={{ marginTop: 6 }}>
                {landmark.tags.map(tag => <span key={tag} className="route-tag-chip" style={{ color: TAG_COLORS[tag], borderColor: TAG_COLORS[tag] }}>{tag}</span>)}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <button
              className="event-share-btn"
              title="Share landmark"
              onClick={() => shareLink(`landmark=${landmark.id}`)}
            >
              <ShareIcon />
              Share
            </button>
            {isOwner && (
              <>
                <button className="event-edit-btn" onClick={() => setEditing(true)}>Edit</button>
                <button className="btn-ghost btn-sm" onClick={handleDelete} disabled={deleting} style={{ color: '#e84545' }}>Delete</button>
              </>
            )}
          </div>
          {!isOwner && landmark.creatorId && (
            <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <ReportBlockMenu
                targetType="landmark"
                targetId={landmark.id}
                targetUserId={landmark.creatorId}
                targetLabel="landmark"
                onReported={() => { onDelete?.(landmark.id); onClose(); }}
                onBlocked={() => { onDelete?.(landmark.id); onClose(); }}
              />
            </div>
          )}
        </div>
        <div className="route-detail-tabs">
          {['details', 'reviews', 'gallery'].map(tab => (
            <button key={tab} className={`route-detail-tab${activeTab === tab ? ' active' : ''}`} onClick={() => setActiveTab(tab)}>
              {tab === 'details' ? 'Details' : tab === 'reviews' ? `Reviews${landmark.reviewCount > 0 ? ` (${landmark.reviewCount})` : ''}` : 'Gallery'}
            </button>
          ))}
        </div>
        <div className="route-detail-body">
          {activeTab === 'details' && (
            <div className="route-tab-details">
              {landmark.description ? <p className="route-detail-desc">{landmark.description}</p> : <p className="route-empty-hint">No description provided.</p>}
            </div>
          )}
          {activeTab === 'reviews' && (
            <div>
              <div className="route-review-form">
                <div style={{ fontWeight: 600, marginBottom: 8, fontSize: '0.9rem' }}>{myReview ? 'Edit your review' : 'Leave a review'}</div>
                <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                  {[1,2,3,4,5].map(n => (
                    <button key={n} type="button" onClick={() => setReviewForm(f => ({ ...f, rating: n }))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.3rem', color: n <= reviewForm.rating ? '#f5a623' : '#3a4460', padding: 0 }}>★</button>
                  ))}
                </div>
                <textarea className="event-form-input event-form-textarea" rows={3}
                  value={reviewForm.comment} onChange={e => setReviewForm(f => ({ ...f, comment: e.target.value }))}
                  placeholder="Share your experience…" style={{ marginBottom: 10 }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn-primary btn-sm" onClick={handleSubmitReview} disabled={submittingReview || !reviewForm.rating}>
                    {submittingReview ? 'Saving…' : myReview ? 'Update' : 'Submit'}
                  </button>
                  {myReview && <button className="btn-ghost btn-sm" onClick={() => handleDeleteReview(myReview.id)} style={{ color: '#e84545' }}>Delete</button>}
                </div>
              </div>
              <div className="route-review-list">
                {reviews.filter(r => !myReview || r.id !== myReview.id).map(r => {
                  const rAuthorId = r.author?.id || r.userId;
                  const isOwnReview = rAuthorId === currentUserId;
                  return (
                    <div key={r.id} className="route-review-card">
                      <div className="route-review-author">
                        <div className="route-review-avatar-placeholder">
                          {r.author?.profileImageUrl ? <img src={r.author.profileImageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} /> : r.author?.username?.[0]?.toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{r.author?.username}</div>
                          <div style={{ color: '#f5a623', fontSize: '0.8rem' }}>{starStr(parseFloat(r.rating))}</div>
                        </div>
                        <div style={{ marginLeft: 'auto', color: '#5a657a', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span>{new Date(r.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                          {!isOwnReview && rAuthorId && (
                            <ReportBlockMenu
                              targetType="landmark_review"
                              targetId={r.id}
                              targetUserId={rAuthorId}
                              targetLabel="review"
                              onReported={() => setReviews(prev => prev.filter(x => x.id !== r.id))}
                              onBlocked={(blockedUserId) => setReviews(prev => prev.filter(x => (x.author?.id || x.userId) !== blockedUserId))}
                              triggerSize="small"
                            />
                          )}
                        </div>
                      </div>
                      {r.comment && <p style={{ margin: '8px 0 0', fontSize: '0.85rem', color: '#c9ced6', lineHeight: 1.5 }}>{r.comment}</p>}
                    </div>
                  );
                })}
                {reviews.length === 0 && <p className="route-empty-hint">No reviews yet. Be the first!</p>}
              </div>
            </div>
          )}
          {activeTab === 'gallery' && (
            <div>
              <div style={{ marginBottom: 14 }}>
                <input ref={photoInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleAddPhoto} />
                <button className="btn-primary btn-sm" onClick={() => photoInputRef.current?.click()}>+ Add Photos</button>
              </div>
              {galleryPhotos.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                  {galleryPhotos.map((p, i) => {
                    const canDelete = isOwner || p.userId === currentUserId;
                    const isMine = p.userId === currentUserId;
                    return (
                      <div key={p.id} style={{ position: 'relative', aspectRatio: '1', cursor: 'pointer', borderRadius: 8, overflow: 'hidden', background: '#1a2030' }}>
                        <img src={photoThumb(p)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onClick={() => setLightboxIdx(i)} />
                        {canDelete && (
                          <button onClick={e => { e.stopPropagation(); handleDeletePhoto(p); }} style={{ position: 'absolute', top: 4, left: 4, background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', borderRadius: 4, width: 22, height: 22, cursor: 'pointer', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                        )}
                        {!isMine && (
                          <div style={{ position: 'absolute', top: 4, right: 4 }} onClick={e => e.stopPropagation()}>
                            <ReportBlockMenu
                              targetType="landmark_photo"
                              targetId={p.id}
                              targetUserId={p.userId}
                              targetLabel="photo"
                              hideBlock={!p.uploader}
                              triggerSize="small"
                              onReported={handleReportedPhoto}
                              onBlocked={(uid) => setGalleryPhotos(prev => prev.filter(x => x.userId !== uid))}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : <p className="route-empty-hint">No photos yet.</p>}
            </div>
          )}
        </div>
        {lightboxIdx !== null && galleryPhotos[lightboxIdx] && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setLightboxIdx(null)}>
            <img src={galleryPhotos[lightboxIdx].imageUrl} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8 }} onClick={e => e.stopPropagation()} />
            <button onClick={() => setLightboxIdx(null)} style={{ position: 'fixed', top: 20, right: 20, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 6, width: 36, height: 36, cursor: 'pointer', fontSize: '1.1rem' }}>✕</button>
            {lightboxIdx > 0 && <button onClick={e => { e.stopPropagation(); setLightboxIdx(i => i - 1); }} style={{ position: 'fixed', left: 20, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 6, width: 36, height: 36, cursor: 'pointer', fontSize: '1.2rem' }}>‹</button>}
            {lightboxIdx < galleryPhotos.length - 1 && <button onClick={e => { e.stopPropagation(); setLightboxIdx(i => i + 1); }} style={{ position: 'fixed', right: 20, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 6, width: 36, height: 36, cursor: 'pointer', fontSize: '1.2rem' }}>›</button>}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Sort Dropdown ────────────────────────────────────────────────────────────
const RouteFilterDropdown = ({ activeTypes, toggleType, activeTags, toggleTag, resetTags }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const typeDefault = activeTypes.size === 2;
  const activeCount = (typeDefault ? 0 : 1) + activeTags.size;

  const check = (
    <svg className="route-sort-check" width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M2 6l3 3 5-5" stroke="#4a9eff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  return (
    <div className="route-sort-dropdown" ref={ref}>
      <button className="route-sort-trigger" onClick={() => setOpen(o => !o)}>
        <svg className="route-sort-trigger-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 4h18M6 12h12M10 20h4"/>
        </svg>
        <span className="route-sort-trigger-label">
          Filter{activeCount > 0 ? <> · <strong>{activeCount}</strong></> : ''}
        </span>
        <svg className={`route-sort-chevron${open ? ' open' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div className="route-sort-menu route-filter-menu">
          <div className="route-sort-section-label">Show</div>
          {['routes', 'landmarks'].map(type => {
            const active = activeTypes.has(type);
            return (
              <button
                key={type}
                className={`route-sort-item${active ? ' active' : ''}`}
                onMouseDown={() => toggleType(type)}
              >
                <span className="route-sort-item-label">{type === 'routes' ? 'Routes' : 'Landmarks'}</span>
                {active && check}
              </button>
            );
          })}

          <div className="route-sort-divider" />

          <div className="route-sort-section-label">
            Tags
            {activeTags.size > 0 && (
              <button className="route-sort-section-clear" onMouseDown={resetTags}>Clear</button>
            )}
          </div>
          <button
            className={`route-sort-item${activeTags.size === 0 ? ' active' : ''}`}
            onMouseDown={() => { if (activeTags.size > 0) resetTags(); }}
          >
            <span className="route-sort-item-label">All Tags</span>
            {activeTags.size === 0 && check}
          </button>
          {ROUTE_TAGS.map(tag => {
            const active = activeTags.has(tag);
            const color = TAG_COLORS[tag];
            return (
              <button key={tag}
                className={`route-sort-item${active ? ' active' : ''}`}
                onMouseDown={() => toggleTag(tag)}
              >
                <span className="route-sort-item-icon" style={{ color }}>#</span>
                <span className="route-sort-item-label">{tag}</span>
                {active && check}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const RouteSortDropdown = ({ sortBy, setSortBy }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const active = SORT_OPTIONS.find(o => o.key === sortBy);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="route-sort-dropdown" ref={ref}>
      <button className="route-sort-trigger" onClick={() => setOpen(o => !o)}>
        <span className="route-sort-trigger-icon">{active?.icon}</span>
        <span className="route-sort-trigger-label">Sort: <strong>{active?.label}</strong></span>
        <svg className={`route-sort-chevron${open ? ' open' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div className="route-sort-menu">
          {SORT_OPTIONS.map(opt => (
            <button key={opt.key}
              className={`route-sort-item${sortBy === opt.key ? ' active' : ''}`}
              onClick={() => { setSortBy(opt.key); setOpen(false); }}
            >
              <span className="route-sort-item-icon">{opt.icon}</span>
              <span className="route-sort-item-label">{opt.label}</span>
              {sortBy === opt.key && (
                <svg className="route-sort-check" width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6l3 3 5-5" stroke="#4a9eff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Type Filter Dropdown ─────────────────────────────────────────────────────
const TypeFilterDropdown = ({ activeTypes, toggleType }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const both = activeTypes.has('routes') && activeTypes.has('landmarks');
  const label = both ? 'All Types' : activeTypes.has('routes') ? 'Routes' : activeTypes.has('landmarks') ? 'Landmarks' : 'All Types';

  const TYPES = [
    { key: 'routes', label: 'Routes' },
    { key: 'landmarks', label: 'Landmarks' },
  ];

  return (
    <div className="route-sort-dropdown" ref={ref}>
      <button className="route-sort-trigger" onClick={() => setOpen(o => !o)}>
        <span className="route-sort-trigger-label"><strong>{label}</strong></span>
        <svg className={`route-sort-chevron${open ? ' open' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && (
        <div className="route-sort-menu">
          <button className={`route-sort-item${both ? ' active' : ''}`}
            onClick={() => { if (!activeTypes.has('routes')) toggleType('routes'); if (!activeTypes.has('landmarks')) toggleType('landmarks'); }}>
            <span className="route-sort-item-label">All Types</span>
            {both && <svg className="route-sort-check" width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="#4a9eff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
          </button>
          {TYPES.map(({ key, label: tLabel }) => {
            const active = activeTypes.has(key) && !both;
            return (
              <button key={key} className={`route-sort-item${active ? ' active' : ''}`}
                onMouseDown={() => {
                  // clicking a single type: if both active, show only this one; else toggle
                  if (both) { toggleType(key === 'routes' ? 'landmarks' : 'routes'); }
                  else { toggleType(key === 'routes' ? 'landmarks' : 'routes'); }
                }}>
                <span className="route-sort-item-label">{tLabel}</span>
                {active && <svg className="route-sort-check" width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="#4a9eff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Create Button Dropdown ───────────────────────────────────────────────────
const CreateDropdown = ({ onCreateRoute, onCreateLandmark }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <button className="btn-primary routes-create-btn" onClick={() => setOpen(o => !o)}>
        + Create
        <svg style={{ marginLeft: 6 }} className={`route-sort-chevron${open ? ' open' : ''}`} width="11" height="11" viewBox="0 0 12 12" fill="none">
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && (
        <div className="route-sort-menu" style={{ right: 0, left: 'auto', top: 'calc(100% + 6px)', minWidth: 160 }}>
          <button className="route-sort-item" onMouseDown={() => { setOpen(false); onCreateRoute(); }}>
            <span className="route-sort-item-label">Route</span>
          </button>
          <button className="route-sort-item" onMouseDown={() => { setOpen(false); onCreateLandmark(); }}>
            <span className="route-sort-item-label">Landmark</span>
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Routes + Landmarks Map ──────────────────────────────────────────────────
const makeLandmarkMapPin = (color) => {
  const PIN_W = 22;
  const PIN_H = 30;
  const pinSvg = `<svg width="${PIN_W}" height="${PIN_H}" viewBox="0 0 22 30" xmlns="http://www.w3.org/2000/svg" style="filter:drop-shadow(0 2px 4px rgba(0,0,0,0.6));">
    <path d="M11 0C4.9 0 0 4.9 0 11c0 7.4 9.5 17.4 10.4 18.3.3.3.9.3 1.2 0C12.5 28.4 22 18.4 22 11 22 4.9 17.1 0 11 0z" fill="${color}" stroke="#fff" stroke-width="1.5"/>
    <circle cx="11" cy="11" r="3.5" fill="#fff"/>
  </svg>`;
  return L.divIcon({
    className: '',
    html: pinSvg,
    iconSize: [PIN_W, PIN_H],
    iconAnchor: [PIN_W / 2, PIN_H],
    popupAnchor: [0, -PIN_H],
  });
};

const RoutesLandmarksMap = ({ items, onItemClick }) => {
  const [selected, setSelected] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [locationReady, setLocationReady] = useState(false);

  useEffect(() => {
    requestCoords()
      .then(({ lat, lng }) => setUserLocation([lat, lng]))
      .catch(() => {})
      .finally(() => setLocationReady(true));
  }, []);

  const routesWithGeom = items.filter(i =>
    i._type === 'route' && i.routeGeometry?.coordinates?.length > 1
  );
  const landmarksWithCoords = items.filter(i =>
    i._type === 'landmark' && i.latitude && i.longitude
  );

  const centre = userLocation || [-25.2744, 133.7751];
  const zoom = userLocation ? 12 : 4;

  if (!locationReady) {
    return (
      <div className="places-map-container">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="places-map-container">
      <MapContainer
        center={centre}
        zoom={zoom}
        style={{ width: '100%', height: '100%' }}
        zoomControl={true}
      >
        <TileLayer
          url={`https://api.mapbox.com/styles/v1/mapbox/dark-v11/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`}
          tileSize={512}
          zoomOffset={-1}
          attribution="&copy; Mapbox"
        />

        {/* Route polylines — invisible thick hit-line beneath the visible line so clicks register easily */}
        {routesWithGeom.map(route => {
          const color = pickItemColor(route);
          const positions = route.routeGeometry.coordinates.map(([lng, lat]) => [lat, lng]);
          const handlers = {
            click: () => setSelected({ ...route, _type: 'route' }),
          };
          return (
            <React.Fragment key={`rt-${route.id}`}>
              <Polyline
                positions={positions}
                pathOptions={{ color: '#000', weight: 14, opacity: 0, lineCap: 'round' }}
                eventHandlers={handlers}
              />
              <Polyline
                positions={positions}
                pathOptions={{ color, weight: 5, opacity: 0.9, lineCap: 'round', lineJoin: 'round' }}
                eventHandlers={handlers}
              />
            </React.Fragment>
          );
        })}

        {/* Landmark pins */}
        {landmarksWithCoords.map(lm => {
          const color = pickItemColor(lm);
          return (
            <Marker
              key={`lm-${lm.id}`}
              position={[parseFloat(lm.latitude), parseFloat(lm.longitude)]}
              icon={makeLandmarkMapPin(color)}
              eventHandlers={{ click: () => setSelected({ ...lm, _type: 'landmark' }) }}
            />
          );
        })}
      </MapContainer>

      {selected && (() => {
        const color = pickItemColor(selected);
        const primaryTag = selected.tags?.includes('Touge') ? 'Touge' : selected.tags?.[0];
        return (
          <div className="places-map-callout" onClick={() => onItemClick(selected)} role="button">
            <div className="places-map-callout-body">
              <div className="places-map-callout-head">
                <span className="places-map-callout-chip" style={{ color }}>
                  {selected._type === 'route' ? 'Route' : 'Landmark'}
                  {primaryTag ? ` · ${primaryTag}` : ''}
                </span>
                <button
                  className="places-map-callout-close"
                  onClick={(e) => { e.stopPropagation(); setSelected(null); }}
                  aria-label="Close"
                >×</button>
              </div>
              <div className="places-map-callout-title">{selected.name || 'Untitled'}</div>
              <div className="places-map-callout-meta">
                {selected._type === 'route' ? (
                  <>
                    {selected.distanceKm && (
                      <span className="places-map-callout-meta-item">{parseFloat(selected.distanceKm).toFixed(1)} km</span>
                    )}
                    {selected.difficulty && (
                      <span
                        className="places-map-callout-meta-item"
                        style={{ color: DIFFICULTY_COLORS[selected.difficulty] || 'rgba(255,255,255,0.78)', textTransform: 'capitalize' }}
                      >{selected.difficulty}</span>
                    )}
                    {selected.reviewCount > 0 && (
                      <span className="places-map-callout-meta-item" style={{ color: '#f5a623' }}>
                        ★ {parseFloat(selected.avgRating).toFixed(1)} ({selected.reviewCount})
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    {selected.address && (
                      <span className="places-map-callout-meta-item" style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {selected.address}
                      </span>
                    )}
                    {selected.reviewCount > 0 && (
                      <span className="places-map-callout-meta-item" style={{ color: '#f5a623' }}>
                        ★ {parseFloat(selected.avgRating).toFixed(1)} ({selected.reviewCount})
                      </span>
                    )}
                  </>
                )}
              </div>
              <div className="places-map-callout-cta">
                View details
                <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                  <path d="M3 6h6m-3-3l3 3-3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

// ─── Main Routes + Landmarks Page ────────────────────────────────────────────
const Routes = () => {
  const { user } = useSelector(s => s.auth);
  const location = useLocation();
  const [routes, setRoutes] = useState([]);
  const [landmarks, setLandmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showCreateRoute, setShowCreateRoute] = useState(false);
  const [showCreateLandmark, setShowCreateLandmark] = useState(false);
  const [selected, setSelected] = useState(null); // { ...item, _type: 'route'|'landmark' }
  const [sortBy, setSortBy] = useState('featured');
  const [activeTags, setActiveTags] = useState(new Set());
  const [activeTypes, setActiveTypes] = useState(new Set(['routes', 'landmarks']));
  const [userCoords, setUserCoords] = useState(null);
  const [viewMode, setViewMode] = useState('grid');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [routeRes, landmarkRes] = await Promise.all([
        api.get('/routes', { params: { limit: 1000 } }),
        api.get('/landmarks', { params: { limit: 1000 } }),
      ]);
      setRoutes((routeRes.data.routes || []).map(r => ({ ...r, _type: 'route' })));
      setLandmarks((landmarkRes.data.landmarks || []).map(lm => ({ ...lm, _type: 'landmark' })));
    } catch {
      // A failed fetch used to fall through to "Nothing found", which reads as
      // "no routes exist" rather than "we couldn't reach the server".
      setLoadError(true);
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Open a specific route or landmark when ?route=ID / ?landmark=ID is in the
  // URL (notification deep-link).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const routeId = params.get('route');
    const landmarkId = params.get('landmark');
    if (routeId) {
      api.get(`/routes/${routeId}`)
        .then(({ data }) => {
          const r = data.route || data;
          if (r) setSelected({ ...r, _type: 'route' });
        })
        .catch(() => {});
    } else if (landmarkId) {
      api.get(`/landmarks/${landmarkId}`)
        .then(({ data }) => {
          const lm = data.landmark || data;
          if (lm) setSelected({ ...lm, _type: 'landmark' });
        })
        .catch(() => {});
    }
  }, [location.search]);

  // Shared-link arrivals on iOS/Android prefer the native app. App.jsx attempts
  // the silent native open before the auth gate; here we surface a manual
  // "Open in app" banner as a fallback (in-app browsers, pasted URLs, search).
  const [showAppBanner, setShowAppBanner] = useState(false);
  useEffect(() => {
    setShowAppBanner(isMobileSharedLink(location.pathname, location.search));
  }, [location.pathname, location.search]);

  useEffect(() => {
    const cached = getCachedCoords();
    if (cached) {
      setUserCoords({ lat: cached.lat, lon: cached.lng });
      return;
    }

    requestCoords()
      .then(pos => setUserCoords({ lat: pos.lat, lon: pos.lng }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (sortBy === 'nearest' && !userCoords) {
      requestCoords()
        .then(pos => setUserCoords({ lat: pos.lat, lon: pos.lng }))
        .catch(() => toast.error('Location access denied — cannot sort by nearest'));
    }
  }, [sortBy, userCoords]);

  const handleUpdate = (updated) => {
    setRoutes(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated } : r));
    setSelected(prev => ({ ...prev, ...updated }));
  };

  const toggleTag = (tag) => setActiveTags(prev => {
    const next = new Set(prev);
    next.has(tag) ? next.delete(tag) : next.add(tag);
    return next;
  });

  const toggleType = (type) => setActiveTypes(prev => {
    const next = new Set(prev);
    if (next.has(type)) {
      // Don't allow unchecking the last one — restore "show both"
      if (next.size === 1) return new Set(['routes', 'landmarks']);
      next.delete(type);
    } else {
      next.add(type);
    }
    return next;
  });

  const resetTags = () => setActiveTags(new Set());

  const tagFiltered = (items) =>
    activeTags.size === 0 ? items : items.filter(r => (r.tags || []).some(t => activeTags.has(t)));

  const allItems = [
    ...(activeTypes.has('routes') ? tagFiltered(routes) : []),
    ...(activeTypes.has('landmarks') ? tagFiltered(landmarks) : []),
  ];

  const routeItems = allItems.filter(i => i._type === 'route');
  const landmarkItems = allItems.filter(i => i._type === 'landmark');
  const sortedRoutes = sortRoutes(routeItems, sortBy, userCoords);
  const showingBoth = activeTypes.has('routes') && activeTypes.has('landmarks');

  // Featured (and other universally meaningful sorts) rank routes and landmarks
  // against each other via the same featuredScore so a great nearby landmark
  // beats a so-so far route. Type-specific sorts (e.g. distance) fall
  // back to the older interleave-by-date hybrid.
  let displayed;
  if (showingBoth && sortBy === 'featured') {
    displayed = [...routeItems, ...landmarkItems].sort((a, b) =>
      featuredScore(b, userCoords) - featuredScore(a, userCoords)
    );
  } else if (showingBoth) {
    const sortedLandmarks = [...landmarkItems].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    displayed = [...sortedRoutes, ...sortedLandmarks].sort((a, b) => {
      if (a._type === 'route' && b._type === 'route') return 0; // preserve sortedRoutes order
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  } else if (activeTypes.has('routes')) {
    displayed = sortedRoutes;
  } else {
    displayed = [...landmarkItems].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }

  return (
    <div className="container page">
      {showAppBanner && (
        <div className="open-in-app-banner">
          <div className="open-in-app-banner-text">
            <strong>MyCarCollection</strong>
            <span>Open this in the app</span>
          </div>
          <button
            className="open-in-app-banner-btn"
            onClick={() => openInApp(location.pathname, location.search)}
          >Open</button>
          <button
            className="open-in-app-banner-dismiss"
            onClick={() => setShowAppBanner(false)}
            aria-label="Dismiss"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}
      <div className="events-header">
        <div className="events-header-left" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', paddingTop: 0 }}>
          <RouteFilterDropdown
            activeTypes={activeTypes}
            toggleType={toggleType}
            activeTags={activeTags}
            toggleTag={toggleTag}
            resetTags={resetTags}
          />
          {viewMode === 'grid' && <RouteSortDropdown sortBy={sortBy} setSortBy={setSortBy} />}
        </div>
        <div className="events-header-right">
          <div className="events-view-toggle">
            <button
              className={`events-view-btn${viewMode === 'grid' ? ' active' : ''}`}
              onClick={() => setViewMode('grid')}
            >Grid</button>
            <button
              className={`events-view-btn${viewMode === 'map' ? ' active' : ''}`}
              onClick={() => setViewMode('map')}
            >Map</button>
          </div>
          <CreateDropdown
            onCreateRoute={() => setShowCreateRoute(true)}
            onCreateLandmark={() => setShowCreateLandmark(true)}
          />
        </div>
      </div>

      {viewMode === 'map' ? (
        <RoutesLandmarksMap items={displayed} onItemClick={setSelected} />
      ) : loading ? (
        <div className="loading-page"><div className="loading-spinner" /></div>
      ) : loadError ? (
        <LoadError
          title="Couldn’t load roads"
          message="We couldn’t reach the server. Check your connection and try again."
          onRetry={load}
        />
      ) : displayed.length === 0 ? (
        <div className="empty-state" style={{ marginTop: 48 }}>
          <div className="empty-icon">—</div>
          <h3>Nothing found</h3>
          <p style={{ color: '#5a657a', fontSize: '0.875rem' }}>Try adjusting your filters.</p>
        </div>
      ) : (
        <div className="routes-grid">
          {displayed.map(item =>
            item._type === 'landmark'
              ? <LandmarkCard key={`lm-${item.id}`} landmark={item} onClick={setSelected} />
              : <RouteCard key={`rt-${item.id}`} route={item} onClick={() => setSelected(item)} />
          )}
        </div>
      )}

      {showCreateRoute && (
        <CreateRouteModal onClose={() => setShowCreateRoute(false)} onCreate={(r) => setRoutes(prev => [{ ...r, _type: 'route' }, ...prev])} />
      )}

      {showCreateLandmark && (
        <CreateLandmarkModal onClose={() => setShowCreateLandmark(false)} onCreate={(lm) => setLandmarks(prev => [{ ...lm, _type: 'landmark' }, ...prev])} />
      )}

      {selected?._type === 'route' && (
        <RouteDetailModal
          route={selected}
          currentUserId={user?.id}
          onClose={() => setSelected(null)}
          onUpdate={handleUpdate}
          onDelete={(id) => { setRoutes(prev => prev.filter(r => r.id !== id)); setSelected(null); }}
        />
      )}

      {selected?._type === 'landmark' && (
        <LandmarkDetailModal
          landmark={selected}
          currentUserId={user?.id}
          onClose={() => setSelected(null)}
          onDelete={(id) => { setLandmarks(prev => prev.filter(lm => lm.id !== id)); setSelected(null); }}
          onUpdate={(updated) => {
            setLandmarks(prev => prev.map(lm => lm.id === updated.id ? { ...lm, ...updated, _type: 'landmark' } : lm));
            setSelected(prev => ({ ...prev, ...updated, _type: 'landmark' }));
          }}
        />
      )}
    </div>
  );
};

// ─── Embeddable route view (full tabs) used inside Event modal ───────────────
export const EmbeddedRouteView = ({ route: initialRoute, currentUserId }) => {
  const [route, setRoute] = useState(initialRoute);
  const [activeTab, setActiveTab] = useState('details');
  const [reviews, setReviews] = useState([]);
  const [myReview, setMyReview] = useState(null);
  const [reviewsLoaded, setReviewsLoaded] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 0, comment: '', fuelRating: 0, foodRating: 0, toiletRating: 0, viewsRating: 0 });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const photoInputRef = useRef();

  const mapImg = route.thumbnailUrl || routeStaticMapUrl(route);
  const googleMapsUrl = buildGoogleMapsUrl(route);

  useEffect(() => {
    api.get(`/routes/${route.id}/reviews`).then(({ data }) => {
      setReviews(data.reviews || []);
      setMyReview(data.myReview || null);
      if (data.myReview) setReviewForm({ rating: parseFloat(data.myReview.rating) || 0, comment: data.myReview.comment || '', fuelRating: parseFloat(data.myReview.fuelRating) || 0, foodRating: parseFloat(data.myReview.foodRating) || 0, toiletRating: parseFloat(data.myReview.toiletRating) || 0, viewsRating: parseFloat(data.myReview.viewsRating) || 0 });
      setReviewsLoaded(true);
    }).catch(() => setReviewsLoaded(true));
  }, [route.id]);

  useEffect(() => {
    api.get(`/routes/${route.id}/photos`)
      .then(({ data }) => setGalleryPhotos(data.photos || []))
      .catch(() => {});
  }, [route.id]);

  useEffect(() => {
    if (lightboxIdx === null) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') setLightboxIdx(i => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setLightboxIdx(i => Math.min(galleryPhotos.length - 1, i + 1));
      if (e.key === 'Escape') setLightboxIdx(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxIdx, galleryPhotos.length]);

  const handleSubmitReview = async () => {
    if (!reviewForm.rating) return toast.error('Please give an overall star rating');
    setSubmittingReview(true);
    try {
      const { data } = await api.post(`/routes/${route.id}/reviews`, { rating: reviewForm.rating, comment: reviewForm.comment || null, fuelRating: reviewForm.fuelRating || null, foodRating: reviewForm.foodRating || null, toiletRating: reviewForm.toiletRating || null, viewsRating: reviewForm.viewsRating || null });
      setMyReview(data.review);
      setReviews(prev => [data.review, ...prev.filter(r => r.id !== data.review.id)]);
      setRoute(r => ({ ...r, ...data.stats }));
      toast.success(myReview ? 'Review updated!' : 'Review submitted!');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to submit review'); }
    finally { setSubmittingReview(false); }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Delete your review?')) return;
    try {
      const { data } = await api.delete(`/routes/${route.id}/reviews/${reviewId}`);
      setReviews(prev => prev.filter(r => r.id !== reviewId));
      setMyReview(null);
      setReviewForm({ rating: 0, comment: '', fuelRating: 0, foodRating: 0, toiletRating: 0, viewsRating: 0 });
      setRoute(r => ({ ...r, ...data.stats }));
    } catch { toast.error('Failed to delete review'); }
  };

  const handleAddPhoto = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const fd = new FormData();
    files.forEach(f => fd.append('photos', f));
    try {
      const { data } = await api.post(`/routes/${route.id}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setGalleryPhotos(prev => [...prev, ...(data.photos || [])]);
    } catch { toast.error('Failed to upload photos'); }
    e.target.value = '';
  };

  const handleDeletePhoto = async (photo) => {
    if (!window.confirm('Remove this photo?')) return;
    try {
      await api.delete(`/routes/${route.id}/photos/${photo.id}`);
      setGalleryPhotos(prev => prev.filter(p => p.id !== photo.id));
      if (lightboxIdx !== null) setLightboxIdx(null);
    } catch { toast.error('Failed to remove photo'); }
  };

  const handleReportedPhoto = ({ targetId }) => {
    setGalleryPhotos(prev => prev.filter(p => p.id !== targetId));
    if (lightboxIdx !== null) setLightboxIdx(null);
  };

  const isOwner = route.creatorId === currentUserId;

  return (
    <>
      {/* Hero map */}
      {mapImg && (
        <div className="route-detail-map-top">
          <img src={mapImg} alt={route.name} />
        </div>
      )}

      {/* Header */}
      <div className="route-detail-header" style={{ paddingTop: 16 }}>
        <div className="route-detail-header-left">
          <h2 className="route-detail-title">{route.name}</h2>
          <div className="route-detail-meta">
            {route.creator && <span className="route-detail-creator">by {route.creator.username}</span>}
            {route.distanceKm && <span className="route-detail-distance">{fmtKm(route.distanceKm)}</span>}
            {route.reviewCount > 0 && (
              <span className="route-detail-rating">
                <StarRating value={parseFloat(route.avgRating)} size={12} />
                <span>{parseFloat(route.avgRating).toFixed(1)}</span>
                <span className="route-detail-rating-count">({route.reviewCount})</span>
              </span>
            )}
          </div>
          <div className="route-detail-tags">
            {route.tags?.map(tag => (
              <span key={tag} className="route-tag-chip" style={{ color: TAG_COLORS[tag], borderColor: TAG_COLORS[tag] }}>{tag}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="route-detail-tabs">
        {[
          { key: 'details', label: 'Details' },
          { key: 'reviews', label: `Reviews${route.reviewCount > 0 ? ` (${route.reviewCount})` : ''}` },
          { key: 'gallery', label: `Gallery${galleryPhotos.length > 0 ? ` (${galleryPhotos.length})` : ''}` },
        ].map(tab => (
          <button key={tab.key} className={`route-detail-tab${activeTab === tab.key ? ' active' : ''}`} onClick={() => setActiveTab(tab.key)}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab body */}
      <div className="route-detail-body">
        {activeTab === 'details' && (
          <div className="route-tab-details">
            {route.description && <p className="route-detail-desc">{route.description}</p>}
            <div className="route-detail-points">
              <div className="route-detail-point">
                <div className="route-detail-point-dot route-detail-point-dot--start" />
                <div><div className="route-detail-point-label">Start</div><div className="route-detail-point-address">{route.startAddress || 'Unknown'}</div></div>
              </div>
              {(route.waypoints || []).map((wp, i) => (
                <React.Fragment key={i}>
                  <div className="route-detail-connector" />
                  <div className="route-detail-point">
                    <div className="route-detail-point-dot" style={{ background: '#f5a623' }} />
                    <div><div className="route-detail-point-label">Stop {i + 1}</div><div className="route-detail-point-address">{wp.address || 'Unknown'}</div></div>
                  </div>
                </React.Fragment>
              ))}
              <div className="route-detail-connector" />
              <div className="route-detail-point">
                <div className="route-detail-point-dot route-detail-point-dot--end" />
                <div><div className="route-detail-point-label">End</div><div className="route-detail-point-address">{route.endAddress || 'Unknown'}</div></div>
              </div>
            </div>
            {googleMapsUrl && (
              <div className="route-detail-actions">
                <a className="btn-primary" href={googleMapsUrl} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', padding: '9px 18px', fontSize: '0.85rem', fontWeight: 600 }}>
                  Open in Google Maps ↗
                </a>
              </div>
            )}
          </div>
        )}

        {activeTab === 'reviews' && (
          <div className="route-tab-reviews">
            {route.reviewCount > 0 && route.amenityScore != null && (
              <div className="route-amenities-agg">
                <p className="route-amenities-agg-title">Amenities</p>
                {[['Fuel','fuelRating'],['Food','foodRating'],['Toilets','toiletRating'],['Views','viewsRating']].map(([label, key]) => {
                  const rated = reviews.filter(r => r[key]);
                  const avg = rated.length ? rated.reduce((s, r) => s + parseFloat(r[key]), 0) / rated.length : null;
                  return avg != null ? <AmenityBar key={key} label={label} value={avg} /> : null;
                })}
              </div>
            )}
            <div className="route-review-form">
              <div className="route-review-form-title">{myReview ? 'Your Review' : 'Write a Review'}</div>
              <div className="route-review-form-row"><span className="route-review-form-label">Rating</span><StarRating value={reviewForm.rating} onChange={v => setReviewForm(f => ({ ...f, rating: v }))} size={20} /></div>
              <div className="route-review-amenities">
                <p className="route-review-amenities-title">Amenity ratings <span>(optional)</span></p>
                {[['fuelRating','Fuel'],['foodRating','Food'],['toiletRating','Toilets'],['viewsRating','Views']].map(([key, label]) => (
                  <div key={key} className="route-review-form-row"><span className="route-review-form-label">{label}</span><StarRating value={reviewForm[key]} onChange={v => setReviewForm(f => ({ ...f, [key]: v }))} size={16} /></div>
                ))}
              </div>
              <textarea className="event-form-input event-form-textarea" value={reviewForm.comment} onChange={e => setReviewForm(f => ({ ...f, comment: e.target.value }))} placeholder="Share your experience on this route…" rows={3} style={{ marginTop: 10 }} />
              <div className="route-review-form-actions">
                <button className="btn-primary btn-sm" onClick={handleSubmitReview} disabled={submittingReview || !reviewForm.rating}>{submittingReview ? 'Saving…' : myReview ? 'Update' : 'Submit'}</button>
                {myReview && <button className="btn-ghost btn-sm" onClick={() => handleDeleteReview(myReview.id)}>Remove review</button>}
              </div>
            </div>
            {reviewsLoaded && reviews.length > 0 ? (
              <div className="route-review-list">
                {reviews.map(r => {
                  const rAuthorId = r.author?.id || r.userId;
                  const isOwnReview = rAuthorId === currentUserId;
                  return (
                    <div key={r.id} className="route-review-card">
                      <div className="route-review-card-header">
                        <div className="route-review-author">
                          {r.author?.profileImageUrl ? <img src={r.author.profileImageUrl} alt={r.author.username} /> : <div className="route-review-avatar-placeholder">{r.author?.username?.[0]?.toUpperCase()}</div>}
                          <span>{r.author?.username}</span>
                        </div>
                        <div className="route-review-card-right">
                          <StarRating value={parseFloat(r.rating)} size={13} />
                          <span className="route-review-date">{new Date(r.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                          {!isOwnReview && rAuthorId && (
                            <ReportBlockMenu
                              targetType="route_review"
                              targetId={r.id}
                              targetUserId={rAuthorId}
                              targetLabel="review"
                              onReported={() => setReviews(prev => prev.filter(x => x.id !== r.id))}
                              onBlocked={(blockedUserId) => setReviews(prev => prev.filter(x => (x.author?.id || x.userId) !== blockedUserId))}
                              triggerSize="small"
                            />
                          )}
                        </div>
                      </div>
                      {(r.fuelRating || r.foodRating || r.toiletRating || r.viewsRating) && (
                        <div className="route-review-amenity-pills">
                          {r.fuelRating && <span className="route-review-amenity-pill">Fuel {parseFloat(r.fuelRating).toFixed(1)}</span>}
                          {r.foodRating && <span className="route-review-amenity-pill">Food {parseFloat(r.foodRating).toFixed(1)}</span>}
                          {r.toiletRating && <span className="route-review-amenity-pill">Toilets {parseFloat(r.toiletRating).toFixed(1)}</span>}
                          {r.viewsRating && <span className="route-review-amenity-pill">Views {parseFloat(r.viewsRating).toFixed(1)}</span>}
                        </div>
                      )}
                      {r.comment && <p className="route-review-comment">{r.comment}</p>}
                    </div>
                  );
                })}
              </div>
            ) : reviewsLoaded ? <p className="route-empty-hint">No reviews yet — be the first.</p> : null}
          </div>
        )}

        {activeTab === 'gallery' && (
          <div className="route-tab-gallery">
            <div className="route-gallery-upload">
              <button className="btn-ghost btn-sm" onClick={() => photoInputRef.current?.click()}>+ Add Photos</button>
              <input ref={photoInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleAddPhoto} />
            </div>
            {galleryPhotos.length > 0 ? (
              <div className="route-photos-grid">
                {galleryPhotos.map((p, i) => {
                  const isMine = p.userId === currentUserId;
                  return (
                    <div key={p.id} className="route-photo-thumb" style={{ position: 'relative' }}>
                      <img src={photoThumb(p)} alt="" onClick={() => setLightboxIdx(i)} />
                      {!isMine && (
                        <div style={{ position: 'absolute', top: 4, right: 4 }} onClick={e => e.stopPropagation()}>
                          <ReportBlockMenu
                            targetType="route_photo"
                            targetId={p.id}
                            targetUserId={p.userId}
                            targetLabel="photo"
                            hideBlock={!p.uploader}
                            triggerSize="small"
                            onReported={handleReportedPhoto}
                            onBlocked={(uid) => setGalleryPhotos(prev => prev.filter(x => x.userId !== uid))}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : <p className="route-empty-hint">No photos yet — add some to show off this route.</p>}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxIdx !== null && galleryPhotos[lightboxIdx] && (
        <div className="route-lightbox" onClick={() => setLightboxIdx(null)}>
          <div className="route-lightbox-inner" onClick={e => e.stopPropagation()}>
            <img src={galleryPhotos[lightboxIdx].imageUrl} alt="Route" />
            <div className="route-lightbox-controls">
              <button onClick={() => setLightboxIdx(i => Math.max(0, i - 1))} disabled={lightboxIdx === 0}>‹</button>
              <span>{lightboxIdx + 1} / {galleryPhotos.length}</span>
              <button onClick={() => setLightboxIdx(i => Math.min(galleryPhotos.length - 1, i + 1))} disabled={lightboxIdx === galleryPhotos.length - 1}>›</button>
              {(isOwner || galleryPhotos[lightboxIdx].userId === currentUserId) && (
                <button className="route-lightbox-delete" onClick={() => handleDeletePhoto(galleryPhotos[lightboxIdx])}>Delete</button>
              )}
            </div>
            <button className="route-lightbox-close" onClick={() => setLightboxIdx(null)}>✕</button>
          </div>
        </div>
      )}
    </>
  );
};

// ─── Embeddable create-route flow (no overlay) used by Events ────────────────
export const CreateRouteFlow = ({ onCreated, onCancel }) => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', tags: [],
    startInput: '', startAddress: '', startLatitude: null, startLongitude: null,
    endInput: '', endAddress: '', endLatitude: null, endLongitude: null,
    stops: [], distanceKm: '', routeGeometry: null,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post('/routes', {
        name: form.name, description: form.description || null, tags: form.tags,
        startAddress: form.startAddress || form.startInput || null,
        startLatitude: form.startLatitude, startLongitude: form.startLongitude,
        endAddress: form.endAddress || form.endInput || null,
        endLatitude: form.endLatitude, endLongitude: form.endLongitude,
        waypoints: form.stops.filter(s => s.latitude).map(s => ({ address: s.address, latitude: s.latitude, longitude: s.longitude })),
        distanceKm: form.distanceKm ? parseFloat(form.distanceKm) : null,
        routeGeometry: form.routeGeometry,
      });
      onCreated(data.route);
      toast.success('Route created!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create route');
    } finally { setSubmitting(false); }
  };

  if (step === 2) {
    return (
      <div className={`event-modal event-modal--map`} style={{ position: 'relative', maxWidth: '100%', borderRadius: 0, border: 'none', boxShadow: 'none', background: 'transparent' }}>
        <Step2 form={form} setForm={setForm} onBack={() => setStep(1)} onSubmit={submit} submitting={submitting} onClose={onCancel} />
      </div>
    );
  }

  return (
    <div style={{ padding: '0 2px' }}>
      <Step1 form={form} set={set} onNext={() => setStep(2)} onClose={onCancel} />
    </div>
  );
};

export default Routes;
